import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ExternalLink,
  Eye,
  FileText,
  Sparkles,
  Info,
  Loader2,
  Pencil,
  Plus,
  ReceiptText,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Switch } from '@/shared/components/ui/switch';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/shared/lib/cn';
import { currencyFromSp, currencyToSp, formatMoney, parseMoney } from '@/shared/lib/money';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import {
  listActiveCompraleArticulos,
  type ArticuloItem,
} from '@/shared/api/controllers/articulos';
import {
  listActivePercepcionesImpuesto,
  type PercepcionImpuestoItem,
} from '@/shared/api/controllers/percepcionesImpuesto';
import {
  listActiveProveedores,
  type ProveedorItem,
} from '@/shared/api/controllers/proveedores';
import {
  listActiveRetenciones,
  type RetencionItem,
} from '@/shared/api/controllers/retenciones';
import {
  previewRetencion,
  RETENCION_NO_CORRESPONDE_PATHS,
  type RetencionPreviewPath,
} from '@/features/administracion/api';
import {
  createCompra,
  editarCompra,
  getCompraDetalle,
  listCompras,
  type CompraListItem,
} from './api';
import { ParentCompraPicker, restanteOf } from './components/ParentCompraPicker';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  formatIvaRate,
  getCompraIvaOptions,
  ivaOptionValueFor,
} from '@/shared/countries';
import { ErrorDialog } from '@/shared/components/ui/status-dialog';
import {
  deleteDocument,
  extractInvoice,
  uploadDocument,
  type ExtractedInvoiceFields,
} from '@/shared/api/documents';
import { ProcessingOverlay } from '@/shared/components/ui/processing-overlay';

/**
 * Nueva / Editar Compra / Nota de Crédito — shared form.
 *
 * The page operates in three modes based on the URL and selected
 * Comprobante:
 *
 * 1. **Nueva** (default, route `/compras/nueva`) — create a new compra.
 * 2. **Editar** (route `/compras/:id/editar`) — load existing compra +
 *    children, patch on submit via PUT `/api/compras/:id`.
 * 3. **NC** — triggered inside Nueva when the user picks
 *    `Comprobante_CO='Nota de Crédito'`. Surfaces a parent-compra
 *    picker, validates NC total ≤ parent's restante, and submits via
 *    POST `/api/compras` with `notaCreditoID` set.
 *
 * Editing is blocked when the compra is already Pagado or Anulado
 * (PowerApps gate — see Screen_ComprobanteCompras.pa.yaml line 555).
 */

const TIPOS_COMPROBANTE = [
  'Factura A',
  'Factura B',
  'Factura C',
  'Nota de Débito',
  'Nota de Crédito',
  'Otros Comprobantes',
  'Recibo',
] as const;

type HeaderState = {
  proveedor: ProveedorItem | null;
  comprobante: string;
  numeroComprobante: string;
  /** `yyyy-mm-dd` — DatePicker's native contract. */
  fechaComprobante: string;
  /** Optional; `yyyy-mm-dd` or empty. Maps to `FechaFiscal_CO` on save. */
  fechaFiscal: string;
  vencimientoPago: string;
  /** User touched the field at least once — stop auto-filling. */
  vencimientoTouched: boolean;
  moneda: 'Pesos Argentinos' | 'Pesos Mexicanos' | 'Dolares' | '';
  tipoCambio: string;
  observaciones: string;
  debitoAutomatico: boolean;
  llevaFactura: boolean;
};

/**
 * Line draft — mirrors the PowerApps cascade. Categoria is the first
 * step (a distinct `Categoria_ART` value), Cuenta Contable is the
 * second (distinct `CuentaContable_ART` filtered by Categoria), and
 * the picked article's name becomes `descripcion`.
 */
type LineDraft = {
  id: string;
  /**
   * SharePoint item id of the underlying `06.ProductoServicioCompras`
   * row, populated only when loaded from an existing compra (edit mode).
   * New rows created in-session leave this undefined; on submit, rows
   * with `spItemId` become PATCHes, rows without become inserts.
   */
  spItemId?: string;
  articuloId?: string;
  productoServicio: string;   // Categoria
  centroCosto: string;         // Cuenta Contable
  descripcion: string;         // Articulo
  observaciones: string;
  cantidad: number;
  precio: number;
  /** IVA rate as a percentage (e.g. 21, 10.5, 16, 8, 0). 0 = Exento. */
  ivaRate: number;
  /** MX SAT classification — see `getCompraIvaOptions`. AR + general
   *  MX lines stay `'general'`; MX import lines tagged at line-entry
   *  time get `'importacion'` (same 16% math, separate report split). */
  ivaCategoria: 'general' | 'importacion';
  subtotal: number;
  iva: number;
  total: number;
};

type PercepcionDraft = {
  id: string;
  /** SharePoint item id on `07.PercepcionesImpuestosCompras`; see `LineDraft.spItemId`. */
  spItemId?: string;
  percepcionImpuesto: string;
  importe: number;
  observaciones: string;
};

/**
 * Retención staged at compra-creation time. Matches the shape PowerApps
 * wrote into `10.RetencionesPago` with `IDPago_RP='Pendiente'` so the
 * first Pagar Factura finds them pre-populated.
 *
 * In edit mode the form also seeds existing retenciones (both Pendiente
 * and already-Activo) into this same draft type so the admin can SEE
 * them. They're rendered read-only — the edit endpoint doesn't accept
 * retenciones, so allowing changes here would silently lose data. The
 * `existingId` flag is what RetencionesTable keys on to suppress the
 * edit/remove buttons.
 */
type RetencionDraft = {
  id: string;
  tipo: string;
  concepto: RetencionItem | null;
  descripcion: string;
  nroComprobante: string;
  fechaComprobanteIso: string;
  importeISAR: number;
  importeRetenido: number;
  /** SP item id when this row was hydrated from the compra detail. */
  existingId?: string;
  /** `Status_RP` of the existing row (`'Activo'` for paid, `'Pendiente'`). */
  status?: string;
  /** `IDPago_RP` of the existing row — `'Pendiente'` or a real pago id. */
  pagoId?: string;
};

type DialogState =
  | { kind: 'closed' }
  | { kind: 'add-line' }
  | { kind: 'edit-line'; line: LineDraft }
  | { kind: 'add-percepcion' }
  | { kind: 'edit-percepcion'; percepcion: PercepcionDraft }
  | { kind: 'add-retencion' }
  | { kind: 'edit-retencion'; retencion: RetencionDraft };

/** Matches the PowerApps dropdown + our Proveedores form. */
const TIPOS_RETENCION = [
  'Ganancias',
  'Ingresos Brutos',
  'IVA',
  'Seguridad Social',
  'Otros',
] as const;
type TipoRetencion = (typeof TIPOS_RETENCION)[number];

/**
 * PowerApps blocks Confirmar unless ≥1 retención is loaded whenever the
 * comprobante is a "normal" factura. The three types below escape that
 * gate — NCs credit, Factura C has no IVA discrimination, and "Otros"
 * is a catch-all.
 */
const COMPROBANTES_EXENTOS_RETENCION = new Set<string>([
  'Nota de Crédito',
  'Factura C',
  'Otros Comprobantes',
]);

/**
 * Resolve the line's article reference for the picker. The SP source
 * never stored an article id on `06.ProductoServicioCompras` — only
 * `Descripcion_PSC` (the article name as text). Prefer an explicit
 * `articuloId` on the draft when present (future-proofing), then fall
 * back to a name match against `Articulo_ART` so legacy lines pre-fill
 * the picker on edit.
 */
function resolveArticulo(
  articulos: ArticuloItem[],
  draft: { articuloId?: string; descripcion?: string },
): ArticuloItem | null {
  if (draft.articuloId) {
    const byId = articulos.find((a) => a.id === draft.articuloId);
    if (byId) return byId;
  }
  const name = (draft.descripcion ?? '').trim();
  if (!name) return null;
  return articulos.find((a) => (a.fields.Articulo_ART ?? '').trim() === name) ?? null;
}

/**
 * Tolerant parser for `PorcentajeIVA_PSC` — the SP source carries it in
 * many shapes ("21%", "21", "10,5%", "10,5", "10.5", "0", "IVA Exento",
 * null) and the legacy parser only matched two of them, silently
 * snapping every other shape to 21. Normalizes to a numeric rate that
 * the country pack's whitelist can validate against.
 */
function parseIvaRate(text: string | null | undefined, fallback: number): number {
  const raw = (text ?? '').trim();
  if (!raw) return fallback;
  if (/exento/i.test(raw)) return 0;
  // Strip "%" and convert comma decimal → dot.
  const n = Number(raw.replace('%', '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
}

const INITIAL_HEADER: HeaderState = {
  proveedor: null,
  comprobante: '',
  numeroComprobante: '',
  fechaComprobante: '',
  fechaFiscal: '',
  vencimientoPago: '',
  vencimientoTouched: false,
  moneda: '',
  tipoCambio: '',
  observaciones: '',
  debitoAutomatico: false,
  llevaFactura: true,
};

export function NuevaCompraPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const { config: activeCountry } = useActiveCountry();
  const showRetenciones = activeCountry.features.has('retenciones');

  const [header, setHeader] = useState<HeaderState>(INITIAL_HEADER);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [percepciones, setPercepciones] = useState<PercepcionDraft[]>([]);
  const [retenciones, setRetenciones] = useState<RetencionDraft[]>([]);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [parentCompra, setParentCompra] = useState<CompraListItem | null>(null);
  // Nueva-only: buffered file the user picked before the compra row exists.
  // Two-step upload — we need the compra id before we can POST to
  // /api/documents/upload. Keeps the UI one-step from the user's view.
  const [pendingFactura, setPendingFactura] = useState<File | null>(null);
  // Live "Analizando factura…" indicator. The Gemini extraction kicks
  // off automatically when the user picks a PDF or image; this just
  // greys the upload row while the call is in flight so the user
  // knows we're trying to autocomplete the form.
  const [extractingFactura, setExtractingFactura] = useState(false);
  // Tracks which header keys were last populated by the Gemini extractor,
  // so when the user removes the factura we can roll those fields back
  // (the data only made sense in the context of that file).
  const [autoFilledByAI, setAutoFilledByAI] = useState<Set<string>>(new Set());
  // Edit-mode only: when the user clicks the trash on the existing
  // factura, we *stage* the delete instead of executing it. The actual
  // `deleteDocument` call runs after `editarCompra` succeeds, alongside
  // the optional new-file upload. Lets the user back out by clicking
  // "Restaurar" before saving.
  const [pendingFacturaDelete, setPendingFacturaDelete] = useState(false);
  const pendingFacturaInputRef = useRef<HTMLInputElement>(null);
  const [pendingFacturaPreviewOpen, setPendingFacturaPreviewOpen] = useState(false);
  const [pendingFacturaSizeError, setPendingFacturaSizeError] = useState<string | null>(
    null,
  );
  // Pre-submit confirmation gate — the Confirmar/Guardar button opens
  // this dialog instead of calling the mutation directly, so a misclick
  // can't write the compra (or worse, an edit) by accident.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Post-create navigation choice — mirrors PowerApps's PopUp_ConfirmarCompra
  // which asked the user whether to jump straight into the pago flow
  // ("Continuar con el Pago" → Screen_PagarFactura) or return to the
  // compras list ("Pasar Luego" → Screen_ComprobanteCompras). NCs skip
  // this prompt because they don't have an associated pago.
  const [postCreate, setPostCreate] = useState<{
    compraId: string;
    proveedor: string;
    numero: string;
  } | null>(null);
  // Blob URL regenerated whenever the pending file changes. We revoke on
  // cleanup so the browser doesn't leak memory when the user swaps files
  // or abandons the dialog.
  const pendingFacturaUrl = useMemo(
    () => (pendingFactura ? URL.createObjectURL(pendingFactura) : null),
    [pendingFactura],
  );
  useEffect(() => {
    if (!pendingFacturaUrl) return;
    return () => URL.revokeObjectURL(pendingFacturaUrl);
  }, [pendingFacturaUrl]);

  // --- reference data ---
  // Country code in every queryKey so AR/MX caches are isolated.
  const countryCode = activeCountry.code;
  const proveedoresQ = useQuery({
    queryKey: ['compras', countryCode, 'proveedores-activos'],
    queryFn: listActiveProveedores,
    staleTime: 5 * 60_000,
  });
  const articulosQ = useQuery({
    queryKey: ['compras', countryCode, 'articulos-comprables'],
    queryFn: listActiveCompraleArticulos,
    staleTime: 5 * 60_000,
  });
  const percepcionesCatalogQ = useQuery({
    queryKey: ['compras', countryCode, 'percepciones-catalogo'],
    queryFn: listActivePercepcionesImpuesto,
    staleTime: 5 * 60_000,
  });
  const retencionesCatalogQ = useQuery({
    queryKey: ['abm-retenciones', countryCode, 'activos'],
    queryFn: listActiveRetenciones,
    staleTime: 5 * 60_000,
  });
  // Shared cache with the list page — used for duplicate (proveedor+numero) check
  // and as the source for the NC parent-compra picker.
  const existingComprasQ = useQuery({
    queryKey: ['compras', countryCode],
    queryFn: listCompras,
    staleTime: 30_000,
  });

  // --- edit-mode: load the existing compra + children ---
  const detailQ = useQuery({
    queryKey: ['compras', countryCode, 'detalle', editId],
    queryFn: () => getCompraDetalle(editId!),
    enabled: isEdit,
    staleTime: 15_000,
  });

  // Seed the form once when edit-mode data arrives AND the proveedor
  // catalog is available (so we can resolve the matching ProveedorItem
  // by name). We key the seed effect on a sentinel so switching compras
  // via URL re-seeds, but subsequent edits inside the form don't revert.
  const seededForEditId = useMemo(() => {
    // Retenciones seeding needs the catalog too — without it we can't
    // resolve `Concepto_RP` (a concat string) back to a `RetencionItem`
    // for the combobox display. Gating the whole effect avoids a
    // half-seeded form flicker.
    if (!isEdit || !detailQ.data || !proveedoresQ.data || !retencionesCatalogQ.data) {
      return null;
    }
    return editId ?? null;
  }, [isEdit, detailQ.data, proveedoresQ.data, retencionesCatalogQ.data, editId]);

  useEffect(() => {
    if (!seededForEditId || !detailQ.data || !proveedoresQ.data) return;
    const d = detailQ.data;
    const f = d.header.fields;
    const proveedor =
      proveedoresQ.data.find(
        (p) => (p.fields.Nombre_P ?? '').trim() === (f.Proveedor_CO ?? '').trim(),
      ) ?? null;
    setHeader({
      proveedor,
      comprobante: f.Comprobante_CO ?? '',
      numeroComprobante: f.NumeroComprobante_CO ?? '',
      fechaComprobante: arToIso(f.FechaComprobante_CO),
      fechaFiscal: arToIso(f.FechaFiscal_CO),
      vencimientoPago: arToIso(f.VencimientoPago_CO),
      vencimientoTouched: true,
      moneda: (f.Moneda_CO as HeaderState['moneda']) ?? '',
      tipoCambio: f.TipoCambio_CO ?? '',
      observaciones: f.Observaciones_CO ?? '',
      debitoAutomatico: f.DebitoAutomatico_CO === 'SI',
      llevaFactura: f.LlevaFactura_CO === 'SI',
    });
    setLines(
      d.lines
        .filter((l) => (l.fields.Status_PSC ?? '') !== 'Anulado')
        .map((l) => {
          const ivaRate = parseIvaRate(l.fields.PorcentajeIVA_PSC, activeCountry.ivaRateDefault);
          const cantidad = parseMoney(l.fields.Cantidad_PSC) ?? 0;
          const precio = parseMoney(l.fields.Precio_PSC) ?? 0;
          const subtotal = parseMoney(l.fields.MontoTotal_PSC) ?? cantidad * precio;
          const ivaAmount = parseMoney(l.fields.IVA_PSC) ?? (subtotal * ivaRate) / 100;
          return {
            id: l.id,
            spItemId: l.id,
            productoServicio: l.fields.ProductoServicio_PSC ?? '',
            centroCosto: l.fields.CentroCosto_PSC ?? '',
            descripcion: l.fields.Descripcion_PSC ?? '',
            observaciones: l.fields.Observaciones_PSC ?? '',
            cantidad,
            precio,
            ivaRate,
            ivaCategoria:
              l.fields.IvaCategoria_PSC === 'importacion' ? 'importacion' : 'general',
            subtotal,
            iva: ivaAmount,
            total: subtotal + ivaAmount,
          };
        }),
    );
    setPercepciones(
      d.percepciones
        .filter((p) => (p.fields.Status_PIC ?? '') !== 'Anulado')
        .map((p) => ({
          id: p.id,
          spItemId: p.id,
          percepcionImpuesto: p.fields.PercepcionImpuesto_PIC ?? '',
          importe: parseMoney(p.fields.Importe_PIC) ?? 0,
          observaciones: p.fields.Observaciones_PIC ?? '',
        })),
    );
    // Retenciones — seed both Pendiente (carry-over from compra
    // creation) and Activo (already-confirmed via a pago) rows so the
    // admin sees the full picture. Anulados are excluded. They render
    // read-only because the edit endpoint doesn't accept retenciones;
    // changes would silently disappear.
    const catalog = retencionesCatalogQ.data ?? [];
    setRetenciones(
      d.retenciones
        .filter((r) => (r.fields.Status_RP ?? '') !== 'Anulado')
        .map((r) => {
          const concat = (r.fields.Concepto_RP ?? '').trim();
          const concepto =
            catalog.find(
              (c) => (c.fields.ConcatCodigoRegimenConcepto_R ?? '').trim() === concat,
            ) ?? null;
          return {
            id: crypto.randomUUID(),
            existingId: r.id,
            status: r.fields.Status_RP ?? '',
            pagoId: r.fields.IDPago_RP ?? '',
            tipo: r.fields.Tipo_RP ?? 'Ganancias',
            concepto,
            descripcion: r.fields.Descripcion_RP ?? '',
            nroComprobante: r.fields.NroComprobante_RP ?? '',
            fechaComprobanteIso: arToIso(r.fields.FechaComprobante_RP),
            importeISAR: parseMoney(r.fields.ImporteISAR_RP) ?? 0,
            importeRetenido: parseMoney(r.fields.ImporteRetenido_RP) ?? 0,
          };
        }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededForEditId]);

  // --- mode detection ---
  // NC mode is only valid when creating (not editing). Editing a NC is
  // treated as a plain Editar — we don't re-run the parent validation.
  const isNC = !isEdit && header.comprobante === 'Nota de Crédito';
  const hasPagos = isEdit && (detailQ.data?.pagos.some((p) => p.fields.Status_PC !== 'Anulado') ?? false);
  const hasRetenciones =
    isEdit &&
    (detailQ.data?.retenciones.some((r) => r.fields.Status_RP === 'Activo') ?? false);

  // --- auto-fill vencimiento from proveedor CondicionPago_P ---
  useEffect(() => {
    if (!header.proveedor || !header.fechaComprobante || header.vencimientoTouched) return;
    const fecha = isoToDate(header.fechaComprobante);
    if (!fecha) return;
    const cond = (header.proveedor.fields.CondicionPago_P ?? '').trim().toLowerCase();
    const days = (() => {
      if (cond === 'contado') return 0;
      const match = cond.match(/^(\d+)\s*d[ií]as/);
      return match ? Number(match[1]) : null;
    })();
    if (days == null) return;
    const next = new Date(fecha);
    next.setDate(next.getDate() + days);
    setHeader((h) => ({ ...h, vencimientoPago: dateToIso(next) }));
    // We intentionally don't flip vencimientoTouched here — the auto-fill is
    // a suggestion; the user can still change it and that click flips the flag.
  }, [header.proveedor, header.fechaComprobante, header.vencimientoTouched]);

  // --- totals (client-side; server re-derives & validates) ---
  // QA #17: when "Lleva Factura" is OFF the IVA shouldn't accrue at
  // all — there's no factura to discount it from. Force per-line IVA
  // to 0 in the aggregate so the total matches subtotal exactly. The
  // backend mirrors this guard in compraHelpers.ts so the persisted
  // Total_CO matches what the user sees.
  const totales = useMemo(() => {
    const totalBruto = lines.reduce((s, l) => s + l.subtotal, 0);
    const totalIVA = header.llevaFactura
      ? lines.reduce((s, l) => s + l.iva, 0)
      : 0;
    const totalPercepciones = percepciones.reduce((s, p) => s + p.importe, 0);
    const totalImpuestos = totalIVA + totalPercepciones;
    const total = totalBruto + totalImpuestos;
    return { totalBruto, totalIVA, totalPercepciones, totalImpuestos, total };
  }, [lines, percepciones, header.llevaFactura]);

  const moneda = currencyFromSp(header.moneda);

  /**
   * File-pick handler that fires the Gemini extraction in the
   * background. The pendingFactura state is set immediately so the
   * upload UI updates without waiting for the LLM round-trip; the
   * autocomplete patches header fields ONLY when they're currently
   * empty so we never clobber values the user typed by hand.
   *
   * Edit mode: same rule — fields seeded from the existing compra are
   * non-empty, so they stay untouched. The intended use case is when
   * the user replaces a factura on a compra that previously had none
   * (Lleva Factura toggled off → on), or fixes a typo'd number.
   *
   * Failure handling is per-outcome:
   *   - `ok` → patch the form fields that came back populated.
   *   - `quota` → warn the user (free-tier exhausted) so they know
   *     why nothing autocompleted; fall back to manual entry.
   *   - `unavailable` / `network` → silent fallback; the user just
   *     types the values.
   */
  const handleFacturaPick = (file: File) => {
    setPendingFactura(file);
    if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
      return;
    }
    setExtractingFactura(true);
    extractInvoice({ file, mode: 'compra' })
      .then((outcome) => {
        if (outcome.kind === 'ok') {
          if (outcome.result.hasAny) applyExtractedFacturaFields(outcome.result.fields);
          return;
        }
        if (outcome.kind === 'quota') {
          toast.warning('Cuota de IA agotada', {
            description: outcome.message,
            duration: 6000,
          });
        }
        // 'unavailable' / 'network': silent. The user can still type.
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[compras] extractInvoice threw unexpectedly', err);
      })
      .finally(() => setExtractingFactura(false));
  };

  /**
   * Patch the header from extracted Gemini fields. Skips overwrites:
   * any field that already has a value stays untouched. Proveedor
   * matching prefers CUIT (exact, deterministic) over name (fuzzy).
   */
  const applyExtractedFacturaFields = (fields: ExtractedInvoiceFields) => {
    const filled: string[] = [];
    const filledKeys = new Set<string>();
    setHeader((h) => {
      const next = { ...h };
      if (!next.numeroComprobante.trim() && fields.nroComprobante) {
        next.numeroComprobante = fields.nroComprobante;
        filled.push('Nº comprobante');
        filledKeys.add('numeroComprobante');
      }
      if (!next.fechaComprobante && fields.fechaComprobante) {
        next.fechaComprobante = fields.fechaComprobante;
        filled.push('fecha');
        filledKeys.add('fechaComprobante');
      }
      if (!next.comprobante && fields.tipoComprobante) {
        next.comprobante = fields.tipoComprobante;
        filled.push('tipo de comprobante');
        filledKeys.add('comprobante');
      }
      if (!next.proveedor && (fields.cuit || fields.proveedorNombre)) {
        const list = proveedoresQ.data ?? [];
        const byCuit = fields.cuit
          ? list.find(
              (p) => (p.fields.Identificacion_P ?? '').replace(/\D/g, '') === fields.cuit,
            )
          : null;
        const byName =
          !byCuit && fields.proveedorNombre
            ? list.find(
                (p) =>
                  (p.fields.Nombre_P ?? '').trim().toLowerCase() ===
                  fields.proveedorNombre!.trim().toLowerCase(),
              )
            : null;
        const matched = byCuit ?? byName ?? null;
        if (matched) {
          next.proveedor = matched;
          next.vencimientoTouched = false;
          filled.push('proveedor');
          filledKeys.add('proveedor');
        }
      }
      if (!next.moneda && fields.moneda) {
        next.moneda = fields.moneda;
        filled.push('moneda');
        filledKeys.add('moneda');
      }
      // Only fill vencimiento if (a) the user hasn't typed one and (b)
      // the auto-fill effect hasn't already derived it from the
      // proveedor's CondicionPago_P. Mark touched so the auto-effect
      // doesn't silently overwrite the AI value.
      if (!next.vencimientoPago && fields.vencimientoPago) {
        next.vencimientoPago = fields.vencimientoPago;
        next.vencimientoTouched = true;
        filled.push('vencimiento del pago');
        filledKeys.add('vencimientoPago');
      }
      return next;
    });
    setAutoFilledByAI(filledKeys);
    if (filled.length > 0) {
      toast.success('Datos detectados de la factura', {
        description: `Se completaron: ${filled.join(', ')}.`,
      });
    }
  };

  /**
   * When the user removes the just-uploaded factura, roll back the
   * fields the AI auto-filled. We only clear keys that *we* populated
   * — anything the user typed by hand stays. After a successful clear
   * we also reset the tracking set so a second extraction starts fresh.
   */
  const clearAIFilledFields = () => {
    if (autoFilledByAI.size === 0) return;
    setHeader((h) => {
      const next = { ...h };
      if (autoFilledByAI.has('numeroComprobante')) next.numeroComprobante = '';
      if (autoFilledByAI.has('fechaComprobante')) next.fechaComprobante = '';
      if (autoFilledByAI.has('comprobante')) next.comprobante = '';
      if (autoFilledByAI.has('proveedor')) next.proveedor = null;
      if (autoFilledByAI.has('moneda')) next.moneda = '';
      if (autoFilledByAI.has('vencimientoPago')) {
        next.vencimientoPago = '';
        next.vencimientoTouched = false;
      }
      return next;
    });
    setAutoFilledByAI(new Set());
  };

  // QA #20: surface the (proveedor + número) collision as a per-field
  // error so the admin sees it without having to hover the disabled
  // Confirmar button. Only checks against the loaded compras list, so
  // the message can lag a fresh write — but that's the same window the
  // legacy app worked in, and a backend race still gets caught at
  // create time.
  const duplicateNumeroError = useMemo<string | null>(() => {
    if (isEdit) return null;
    if (!header.proveedor) return null;
    const numero = header.numeroComprobante.trim();
    if (!numero) return null;
    const proveedorName = header.proveedor.fields.Nombre_P?.trim() ?? '';
    const justCreatedId = postCreate?.compraId;
    const dup = (existingComprasQ.data ?? []).find(
      (c) =>
        c.id !== justCreatedId &&
        c.fields.Status_CO !== 'Anulado' &&
        (c.fields.Proveedor_CO ?? '').trim() === proveedorName &&
        (c.fields.NumeroComprobante_CO ?? '').trim() === numero,
    );
    return dup
      ? 'Este número de comprobante ya existe para este proveedor.'
      : null;
  }, [
    isEdit,
    header.proveedor,
    header.numeroComprobante,
    existingComprasQ.data,
    postCreate?.compraId,
  ]);

  // --- totals / parent saldo ---
  const parentRestante = parentCompra ? restanteOf(parentCompra) : 0;

  // --- validations (matches PowerApps Screen_Compras.pa.yaml:107-115) ---
  // Three-branch gate:
  //   1. Lleva Factura OFF → minimal fields (proveedor / fechas / moneda / lines).
  //   2. Lleva Factura ON + Comprobante ∈ {NC, Factura C, Otros} → add
  //      comprobante type, número. No retenciones required.
  //   3. Lleva Factura ON + Comprobante ∉ that set → also require ≥1 retención.
  const validationError = useMemo<string | null>(() => {
    if (!header.proveedor) return 'Seleccioná un proveedor.';
    if (!header.fechaComprobante) return 'Seleccioná la fecha del comprobante.';
    if (!header.vencimientoPago) return 'Seleccioná el vencimiento del pago.';
    const fecha = isoToDate(header.fechaComprobante);
    const vto = isoToDate(header.vencimientoPago);
    if (fecha && vto && vto < fecha) {
      return 'El vencimiento no puede ser anterior a la fecha del comprobante.';
    }
    if (!header.moneda) return 'Seleccioná la moneda.';
    if (header.moneda === 'Dolares') {
      const tc = parseMoney(header.tipoCambio);
      if (!tc || tc <= 0) return 'Ingresá el tipo de cambio.';
    }
    if (lines.length === 0) return 'Agregá al menos un producto o servicio.';

    // Comprobante-specific requirements only kick in when the compra
    // llevaFactura (legally-tracked). Without factura, the admin can
    // register a purchase using just proveedor + fechas + moneda + lines.
    if (header.llevaFactura) {
      if (!header.comprobante) return 'Seleccioná el tipo de comprobante.';
      if (!header.numeroComprobante.trim()) {
        return 'Ingresá el número de comprobante.';
      }
      // Retención gate — matches PowerApps line 108-111. Only applies in
      // countries with the retenciones regime (AR). In MX retenciones
      // don't exist and the panel that adds them isn't rendered, so a
      // Factura A/B would otherwise dead-end Confirmar with no way to
      // satisfy the gate.
      const retencionRequired =
        showRetenciones && !COMPROBANTES_EXENTOS_RETENCION.has(header.comprobante);
      if (retencionRequired && retenciones.length === 0) {
        return 'Agregá al menos una retención (Ganancias / IIBB / etc.).';
      }
    }

    if (isNC) {
      if (!parentCompra) {
        return 'Seleccioná la factura a la que se aplica esta Nota de Crédito.';
      }
      const totalLines = lines.reduce((s, l) => s + l.subtotal, 0);
      const totalPerceps = percepciones.reduce((s, p) => s + p.importe, 0);
      const totalIVA = lines.reduce((s, l) => s + l.iva, 0);
      const ncTotal = totalLines + totalIVA + totalPerceps;
      if (ncTotal > parentRestante + 0.01) {
        return `El total de la NC excede el saldo disponible de la factura (${parentRestante.toFixed(2)}).`;
      }
    }
    // Duplicate check against already-loaded compras — only meaningful
    // when we actually have a number to compare. Skip in edit mode
    // because the existing row itself would match its own (proveedor,
    // numero) and we want to allow unchanged saves.
    if (!isEdit && header.numeroComprobante.trim()) {
      const existing = existingComprasQ.data ?? [];
      const proveedorName = header.proveedor.fields.Nombre_P?.trim() ?? '';
      const numero = header.numeroComprobante.trim();
      const dup = existing.find(
        (c) =>
          c.fields.Status_CO !== 'Anulado' &&
          (c.fields.Proveedor_CO ?? '').trim() === proveedorName &&
          (c.fields.NumeroComprobante_CO ?? '').trim() === numero,
      );
      if (dup) {
        return `Ya existe un comprobante ${numero} para ${proveedorName}.`;
      }
    }
    return null;
  }, [
    header,
    lines,
    percepciones,
    retenciones,
    existingComprasQ.data,
    isEdit,
    isNC,
    parentCompra,
    parentRestante,
    showRetenciones,
  ]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!header.proveedor || !header.moneda) {
        throw new Error('Invalid state');
      }
      const tipoCambio = header.moneda === 'Dolares' ? parseMoney(header.tipoCambio) : null;
      const baseHeader = {
        proveedor: header.proveedor.fields.Nombre_P?.trim() ?? '',
        comprobante: header.comprobante,
        numeroComprobante: header.numeroComprobante.trim(),
        fechaComprobante: isoToAr(header.fechaComprobante),
        fechaFiscal: header.fechaFiscal ? isoToAr(header.fechaFiscal) : '',
        vencimientoPago: isoToAr(header.vencimientoPago),
        moneda: header.moneda,
        tipoCambio: tipoCambio ?? undefined,
        observaciones: header.observaciones.trim() || undefined,
        debitoAutomatico: header.debitoAutomatico,
        llevaFactura: header.llevaFactura,
      };
      const mappedPercepciones = percepciones.map((p) => ({
        id: p.spItemId,
        percepcionImpuesto: p.percepcionImpuesto,
        importe: p.importe,
        observaciones: p.observaciones || undefined,
      }));
      // QA #17: zero out per-line IVA when "Lleva Factura" is OFF so
      // the backend's `derivedIVA = Σ l.iva` matches the client's
      // `totales.totalIVA = 0`. The line's intrinsic ivaRate stays
      // (so re-toggling the flag restores the right amount), but the
      // computed iva amount is suppressed for persistence.
      const ivaSuppressed = !header.llevaFactura;
      const mappedLines = lines.map((l) => ({
        id: l.spItemId,
        articuloId: l.articuloId,
        productoServicio: l.productoServicio,
        centroCosto: l.centroCosto,
        descripcion: l.descripcion,
        observaciones: l.observaciones || undefined,
        cantidad: l.cantidad,
        precio: l.precio,
        ivaRate: ivaSuppressed ? 0 : l.ivaRate,
        ivaCategoria: ivaSuppressed ? 'general' : l.ivaCategoria,
        subtotal: l.subtotal,
        iva: ivaSuppressed ? 0 : l.iva,
        total: ivaSuppressed ? l.subtotal : l.total,
      }));
      if (isEdit && editId) {
        const response = await editarCompra(editId, {
          header: baseHeader,
          lines: mappedLines,
          percepciones: mappedPercepciones,
        });
        // After the row update succeeds, flush staged factura changes.
        // Order matters: delete first (frees the slot), then upload the
        // replacement. Errors here surface as a warning toast — the
        // header/lines edit itself succeeded.
        if (response.outcome === 'edited') {
          if (pendingFacturaDelete) {
            try {
              await deleteDocument('Compra', editId);
            } catch (err) {
              toast.warning('Compra editada, pero no se pudo eliminar la factura', {
                description:
                  err instanceof Error
                    ? err.message
                    : 'Eliminala desde Ver Compra.',
              });
            }
          }
          if (pendingFactura) {
            try {
              await uploadDocument({
                entityType: 'Compra',
                entityId: editId,
                file: pendingFactura,
              });
            } catch (err) {
              toast.warning('Compra editada, pero no se pudo adjuntar la factura', {
                description:
                  err instanceof Error
                    ? `${err.message}. Subila desde Ver Compra.`
                    : 'Subila desde Ver Compra.',
              });
            }
          }
        }
        return { kind: 'edit' as const, response };
      }
      // Retenciones only travel with the payload when the gate actually
      // expects them: llevaFactura on, comprobante not in exempt set.
      // Sending them otherwise would silently pre-load a "Pendiente"
      // retención that the admin never saw on screen.
      const retencionGateApplies =
        header.llevaFactura &&
        !COMPROBANTES_EXENTOS_RETENCION.has(header.comprobante);
      const mappedRetenciones = retencionGateApplies
        ? retenciones.map((r) => ({
            tipo: r.tipo,
            conceptoConcat: r.concepto?.fields.ConcatCodigoRegimenConcepto_R ?? '',
            descripcion: r.descripcion || undefined,
            nroComprobante: r.nroComprobante,
            fechaComprobante: isoToAr(r.fechaComprobanteIso),
            importeISAR: r.importeISAR,
            importeRetenido: r.importeRetenido,
          }))
        : [];
      const response = await createCompra({
        header: baseHeader,
        lines: mappedLines.map(({ id: _id, ...rest }) => rest),
        percepciones: mappedPercepciones.map(({ id: _id, ...rest }) => rest),
        retenciones: mappedRetenciones,
        totales: {
          totalBruto: totales.totalBruto,
          totalIVA: totales.totalIVA,
          totalImpuestos: totales.totalImpuestos,
          totalSinIVA: totales.totalBruto,
          total: totales.total,
        },
        notaCreditoID: isNC ? parentCompra?.id : undefined,
      });

      // Two-step upload: if the user attached a factura file in Nueva
      // mode, ship it now that the compra row exists. We swallow the
      // upload error into a warning toast — the compra itself succeeded
      // and the user can re-upload from Editar Compra.
      if (!('outcome' in response) && pendingFactura) {
        try {
          await uploadDocument({
            entityType: 'Compra',
            entityId: response.id,
            file: pendingFactura,
          });
        } catch (err) {
          toast.warning('Compra creada, pero no se pudo adjuntar la factura', {
            description:
              err instanceof Error
                ? `${err.message}. Subila desde "Editar Compra".`
                : 'Subila desde "Editar Compra".',
          });
        }
      }

      return {
        kind: 'create' as const,
        response,
      };
    },
    onSuccess: ({ kind, response }) => {
      if (kind === 'edit') {
        if (response.outcome === 'edited') {
          const { updated, created, anulated } = response.lines;
          toast.success('Compra actualizada', {
            description: `${updated} editada(s), ${created} nueva(s), ${anulated} quitada(s).`,
          });
          qc.invalidateQueries({ queryKey: ['compras'] });
          qc.invalidateQueries({ queryKey: ['compras', countryCode, 'detalle', editId] });
          navigate(`/compras/${editId}`);
        } else {
          toast.info('No se pudo editar', { description: response.message });
        }
        return;
      }
      // Create path — handle NC outcomes before the success banner.
      if ('outcome' in response) {
        toast.info('No se pudo registrar', { description: response.message });
        return;
      }
      if (response.notaCreditoID) {
        toast.success('Nota de Crédito registrada', {
          description: response.fullOffset
            ? 'La factura quedó saldada por completo.'
            : `Se descontó del saldo pendiente de la factura.`,
        });
        qc.invalidateQueries({ queryKey: ['compras'] });
        // NCs don't have an associated pago — skip the choice dialog and
        // go straight back to the list, matching PowerApps's NC flow.
        navigate('/compras');
        return;
      }
      toast.success('Compra registrada', {
        description: `${response.linesCreadas} línea(s) y ${response.percepcionesCreadas} percepción(es).`,
      });
      qc.invalidateQueries({ queryKey: ['compras'] });
      // Surface the Continuar con el Pago / Pagar Luego choice; the user
      // navigates from there.
      setPostCreate({
        compraId: response.id,
        proveedor: header.proveedor?.fields.Nombre_P ?? '',
        numero: header.numeroComprobante,
      });
    },
    onError: (err) =>
      toast.error(isEdit ? 'No se pudo editar la compra' : 'No se pudo registrar la compra', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const canSubmit = validationError === null && !mutation.isPending;

  // Edit mode: wait for the detail + proveedor catalog before showing
  // the form so the seed effect has a chance to populate everything
  // first-paint — avoids a flash of empty state.
  if (isEdit && (detailQ.isPending || proveedoresQ.isPending)) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  if (isEdit && detailQ.isError) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/compras')} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Volver
        </Button>
        <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center">
          <p className="text-base font-medium text-foreground">Compra no encontrada</p>
          <p className="mt-1 text-sm text-muted-foreground">
            La compra #{editId} no existe o no tenés permisos para editarla.
          </p>
        </div>
      </div>
    );
  }
  if (
    isEdit &&
    detailQ.data &&
    (detailQ.data.header.fields.Status_CO === 'Pagado' ||
      detailQ.data.header.fields.Status_CO === 'Anulado')
  ) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/compras')} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Volver
        </Button>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center">
          <p className="text-base font-medium text-amber-900">No se puede editar</p>
          <p className="mt-1 text-sm text-amber-800">
            Esta compra está en estado {detailQ.data.header.fields.Status_CO} y no admite
            modificaciones.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      {/* Top bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => navigate('/compras')}
          aria-label="Volver a Compras"
          title="Volver a Compras"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          {isEdit
            ? `Editar compra #${editId}`
            : isNC
              ? 'Nueva Nota de Crédito'
              : 'Nueva Compra'}
        </h1>
        <div className="ml-auto flex flex-wrap items-center gap-5">
          {/* Both toggles are read-only in edit mode. Flipping either
              cascades into a lot of structural changes (retenciones
              would have to be cleared/recreated, MovimientoCajas tax
              gates flip on the next pago, comprobante fields toggle
              required) — the edit endpoint doesn't model any of that
              yet, so silent corruption is the alternative. The
              `title` is the keyboard tooltip; the muted label hint
              spells out why for sighted users. */}
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Debito Automático:</span>
            <Switch
              checked={header.debitoAutomatico}
              disabled={isEdit}
              title={
                isEdit
                  ? 'Débito Automático no se puede cambiar desde la edición — afecta los impuestos calculados al pagar.'
                  : undefined
              }
              onCheckedChange={(v) =>
                setHeader((h) => ({ ...h, debitoAutomatico: v }))
              }
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Lleva Factura:</span>
            <Switch
              checked={header.llevaFactura}
              disabled={isEdit}
              title={
                isEdit
                  ? 'Lleva Factura no se puede cambiar desde la edición — alteraría retenciones y la estructura de la compra.'
                  : undefined
              }
              onCheckedChange={(v) => {
                setHeader((h) => ({ ...h, llevaFactura: v }));
                // PowerApps `Tggl_FacturaNegro_CO.OnUncheck` clears
                // `Collect_RetencionesPagoNuevoPago`. Compras without
                // factura don't carry retenciones, so leaving stale
                // rows in state would either be silently dropped at
                // submit (`retencionGateApplies`) or — worse —
                // re-emitted if the user toggles back on. Clearing
                // either way matches PowerApps's invariant.
                //
                // Same logic applies to Percepciones e Impuestos: no
                // factura → no percepciones to declare. The section is
                // hidden visually, so wipe state so a stale draft from
                // a previous toggle-on doesn't get persisted on submit.
                if (!v) {
                  setRetenciones([]);
                  setPercepciones([]);
                }
              }}
            />
          </label>
        </div>
      </div>

      {/* Edit-mode banners */}
      {isEdit && (hasPagos || hasRetenciones) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="space-y-0.5">
              {hasPagos && (
                <p>
                  Esta compra ya tiene pagos registrados. Los cambios en el total no
                  recalcularán los pagos — ajustalos desde Administración si es necesario.
                </p>
              )}
              {hasRetenciones && (
                <p>
                  Tiene retenciones activas. Los cambios en el total no recalcularán las
                  retenciones ya emitidas.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NC parent picker */}
      {isNC && (
        <div className="rounded-xl border bg-card p-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Factura a la que se aplica
              </Label>
              <ParentCompraPicker
                compras={existingComprasQ.data ?? []}
                value={parentCompra}
                onChange={(next) => {
                  setParentCompra(next);
                  // Lock Moneda to the parent's moneda — a NC in a different
                  // currency doesn't net cleanly against the parent saldo.
                  if (next) {
                    setHeader((h) => ({
                      ...h,
                      moneda: (next.fields.Moneda_CO as HeaderState['moneda']) ?? h.moneda,
                    }));
                  }
                }}
                loading={existingComprasQ.isPending}
              />
            </div>
            {parentCompra && (
              <div className="md:ml-3">
                <span className="inline-flex h-9 items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-xs">
                  <span className="text-emerald-700">Saldo disponible</span>
                  <span className="font-semibold text-emerald-900">
                    {formatMoney(
                      parentRestante,
                      currencyFromSp(parentCompra.fields.Moneda_CO),
                    )}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header grid — 12 cols so row 1 (4/4/4) and row 2 (3/3/3/3) stay
          aligned. Dates sit in compact 3-col slots; Moneda shares row 2.
          Tipo de Cambio wraps to its own slot when Dolares is selected. */}
      {/* Header form — sectioned cards with a small uppercase caption
          per group. Layout depends on `llevaFactura`:
            ON  → two cards (Identificación + Fechas y moneda).
            OFF → one combined card (Datos de la compra), since the
                  no-factura case has only Proveedor + dates + moneda
                  and a separate "Identificación" card with a single
                  field reads as wasted vertical space. */}

      {/* SECTION 1 + 2 — when llevaFactura is on, the two cards
          (Identificación + Fechas y moneda) sit side-by-side on lg+
          screens to save vertical space. Below lg they stack
          naturally via flex-col. When llevaFactura is off there's
          only one merged card and the wrapper is a no-op flex column. */}
      <div
        className={cn(
          'flex flex-col gap-4',
          header.llevaFactura && 'lg:grid lg:grid-cols-2',
        )}
      >
      {/* SECTION 1 — Identificación (lleva=on) or Datos de la compra
          (lleva=off, merged with dates/moneda). */}
      <FormCard
        caption={header.llevaFactura ? 'Identificación' : 'Datos de la compra'}
        cols={header.llevaFactura ? 2 : 12}
      >
        <Field
          label="Proveedor"
          className={
            header.llevaFactura
              ? undefined
              : header.moneda === 'Dolares'
                ? 'md:col-span-4'
                : 'md:col-span-5'
          }
        >
          <Combobox
            value={header.proveedor}
            onChange={(p) => setHeader((h) => ({ ...h, proveedor: p, vencimientoTouched: false }))}
            items={proveedoresQ.data ?? []}
            getKey={(p) => p.id}
            getLabel={(p) => p.fields.Nombre_P?.trim() || '—'}
            getSublabel={(p) => p.fields.CondicionPago_P}
            matcher={(p, q) =>
              (p.fields.Nombre_P ?? '').toLowerCase().includes(q) ||
              (p.fields.Identificacion_P ?? '').toLowerCase().includes(q)
            }
            placeholder={
              proveedoresQ.isPending ? 'Cargando…' : 'Seleccionar Proveedor…'
            }
            className="h-12"
          />
        </Field>
        {/* lleva=off: render the dates + moneda inline in this same
            card so we save the vertical space of a second section. */}
        {!header.llevaFactura && (
          <>
            <Field
              label="Fecha de compra"
              className={header.moneda === 'Dolares' ? 'md:col-span-2' : 'md:col-span-3'}
            >
              <DatePicker
                value={header.fechaComprobante}
                onChange={(v) => setHeader((h) => ({ ...h, fechaComprobante: v }))}
                placeholder="Seleccionar…"
                max={header.vencimientoPago || undefined}
                ariaLabel="Fecha de compra"
                className="h-12"
              />
            </Field>
            <Field
              label="Vencimiento del Pago"
              className={header.moneda === 'Dolares' ? 'md:col-span-2' : 'md:col-span-2'}
            >
              <DatePicker
                value={header.vencimientoPago}
                onChange={(v) =>
                  setHeader((h) => ({ ...h, vencimientoPago: v, vencimientoTouched: true }))
                }
                placeholder="—"
                min={header.fechaComprobante || undefined}
                ariaLabel="Vencimiento del pago"
                className="h-12"
              />
            </Field>
            <Field
              label="Moneda"
              className={header.moneda === 'Dolares' ? 'md:col-span-2' : 'md:col-span-2'}
            >
              <Select
                value={header.moneda}
                onValueChange={(v) =>
                  setHeader((h) => ({
                    ...h,
                    moneda: v as HeaderState['moneda'],
                    tipoCambio: v === 'Dolares' ? h.tipoCambio : '',
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {/* Country-aware home currency option — shows the active
                       country's primary moneda (Pesos Argentinos in AR,
                       Pesos Mexicanos in MX). USD stays available in both. */}
                  <SelectItem value={currencyToSp(activeCountry.currency.primary)}>
                    {currencyToSp(activeCountry.currency.primary)} ({activeCountry.currency.primary})
                  </SelectItem>
                  <SelectItem value="Dolares">Dólares (USD)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {header.moneda === 'Dolares' && (
              <Field label="Tipo de Cambio" className="md:col-span-2">
                <Input
                  variant="filled"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={header.tipoCambio}
                  onChange={(e) =>
                    setHeader((h) => ({ ...h, tipoCambio: sanitizeDecimal(e.target.value) }))
                  }
                />
              </Field>
            )}
          </>
        )}
        {header.llevaFactura && (
          <>
            <Field label="Comprobante">
              <Select
                value={header.comprobante}
                onValueChange={(v) => {
                  setHeader((h) => ({ ...h, comprobante: v }));
                  // The retenciones panel is hidden for exempt
                  // comprobantes (NC, Factura C, Otros) — keep state in
                  // sync with the UI so existing rows can't sneak past
                  // the gate or reappear when the user picks a normal
                  // Factura A/B again.
                  if (COMPROBANTES_EXENTOS_RETENCION.has(v)) setRetenciones([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar Tipo…" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_COMPROBANTE.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Número">
              <Input
                variant="filled"
                placeholder="Ingresar Número"
                value={header.numeroComprobante}
                onChange={(e) =>
                  setHeader((h) => ({ ...h, numeroComprobante: e.target.value }))
                }
                aria-invalid={duplicateNumeroError ? true : undefined}
                className={
                  duplicateNumeroError
                    ? 'border-destructive focus-visible:ring-destructive/40'
                    : undefined
                }
              />
              {duplicateNumeroError && (
                <p className="mt-1 text-xs font-medium text-destructive">
                  {duplicateNumeroError}
                </p>
              )}
            </Field>
            <Field label="Factura">
              {isEdit && editId ? (
                detailQ.data?.header.fields.FacturaDriveItemID_CO &&
                detailQ.data.header.fields.FacturaFileName_CO &&
                !pendingFacturaDelete ? (
                  <div
                    className="flex h-12 items-center"
                    title="Para sustituir el archivo, eliminalo y subí uno nuevo. Los cambios se aplican al guardar."
                  >
                    <FileLink
                      entityType="Compra"
                      entityId={editId}
                      fileName={detailQ.data.header.fields.FacturaFileName_CO}
                      onDeleteOverride={() => setPendingFacturaDelete(true)}
                    />
                  </div>
                ) : (
                  <PendingFacturaUploader
                    pending={pendingFactura}
                    inputRef={pendingFacturaInputRef}
                    llevaFactura={header.llevaFactura}
                    showRestore={pendingFacturaDelete}
                    onRestore={() => {
                      setPendingFacturaDelete(false);
                      setPendingFactura(null);
                      if (pendingFacturaInputRef.current)
                        pendingFacturaInputRef.current.value = '';
                    }}
                    onPick={handleFacturaPick}
                    extracting={extractingFactura}
                    onClear={() => {
                      setPendingFactura(null);
                      if (pendingFacturaInputRef.current)
                        pendingFacturaInputRef.current.value = '';
                      clearAIFilledFields();
                    }}
                    onPreview={() => setPendingFacturaPreviewOpen(true)}
                    onSizeError={(msg) => setPendingFacturaSizeError(msg)}
                  />
                )
              ) : (
                <PendingFacturaUploader
                  pending={pendingFactura}
                  inputRef={pendingFacturaInputRef}
                  llevaFactura={header.llevaFactura}
                  showRestore={false}
                  onRestore={() => {}}
                  onPick={handleFacturaPick}
                  extracting={extractingFactura}
                  onClear={() => {
                    setPendingFactura(null);
                    if (pendingFacturaInputRef.current)
                      pendingFacturaInputRef.current.value = '';
                    clearAIFilledFields();
                  }}
                  onPreview={() => setPendingFacturaPreviewOpen(true)}
                  onSizeError={(msg) => setPendingFacturaSizeError(msg)}
                />
              )}
            </Field>
          </>
        )}
      </FormCard>

      {/* SECTION 2 — Fechas y moneda. Only rendered when llevaFactura
          is ON; the no-factura case folds these fields into Section 1
          above to save vertical space. */}
      {header.llevaFactura && (
        <FormCard caption="Fechas y moneda" cols={2}>
          <Field label="Fecha comprobante">
            <DatePicker
              value={header.fechaComprobante}
              onChange={(v) => setHeader((h) => ({ ...h, fechaComprobante: v }))}
              placeholder="Seleccionar…"
              max={header.vencimientoPago || undefined}
              ariaLabel="Fecha comprobante"
              className="h-12"
            />
          </Field>
          <Field label="Fecha fiscal">
            <DatePicker
              value={header.fechaFiscal}
              onChange={(v) => setHeader((h) => ({ ...h, fechaFiscal: v }))}
              placeholder="Opcional"
              ariaLabel="Fecha fiscal"
              className="h-12"
            />
          </Field>
          <Field label="Vencimiento del Pago">
            <DatePicker
              value={header.vencimientoPago}
              onChange={(v) =>
                setHeader((h) => ({ ...h, vencimientoPago: v, vencimientoTouched: true }))
              }
              placeholder="—"
              min={header.fechaComprobante || undefined}
              ariaLabel="Vencimiento del pago"
              className="h-12"
            />
          </Field>
          <Field label="Moneda">
            <Select
              value={header.moneda}
              onValueChange={(v) =>
                setHeader((h) => ({
                  ...h,
                  moneda: v as HeaderState['moneda'],
                  // Clear TC when switching away from Dolares.
                  tipoCambio: v === 'Dolares' ? h.tipoCambio : '',
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar…" />
              </SelectTrigger>
              <SelectContent>
                {/* Country-aware home currency option. */}
                <SelectItem value={currencyToSp(activeCountry.currency.primary)}>
                  {currencyToSp(activeCountry.currency.primary)} ({activeCountry.currency.primary})
                </SelectItem>
                <SelectItem value="Dolares">Dólares (USD)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {header.moneda === 'Dolares' && (
            <Field label="Tipo de Cambio">
              <Input
                variant="filled"
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={header.tipoCambio}
                onChange={(e) =>
                  setHeader((h) => ({ ...h, tipoCambio: sanitizeDecimal(e.target.value) }))
                }
              />
            </Field>
          )}
        </FormCard>
      )}
      </div>

      {/* SECTION 3 — Observaciones. Free-form notes, full width. */}
      <FormCard caption="Observaciones" gridLess>
        <Textarea
          variant="filled"
          rows={3}
          placeholder="Ingresar Observaciones"
          value={header.observaciones}
          onChange={(e) => setHeader((h) => ({ ...h, observaciones: e.target.value }))}
        />
      </FormCard>

      {/* Producto Servicio section */}
      <Section
        title="Producto Servicio"
        onAdd={() => setDialog({ kind: 'add-line' })}
        disabled={isNC && !parentCompra}
      >
        {lines.length === 0 ? (
          <EmptyState
            icon={<ReceiptText className="h-5 w-5" aria-hidden />}
            title={
              isNC && !parentCompra
                ? 'Seleccioná primero la factura a la que se aplica la NC'
                : 'Aun no se agregaron productos o servicios'
            }
            hint={
              isNC && !parentCompra
                ? 'Cuando elijas la factura podrás agregar las líneas de la Nota de Crédito'
                : 'Agrega un producto o servicio para visualizarlo aquí'
            }
          />
        ) : (
          <ProductoTable
            lines={lines}
            moneda={moneda}
            llevaFactura={header.llevaFactura}
            onEdit={(line) => setDialog({ kind: 'edit-line', line })}
            onRemove={(id) => setLines((prev) => prev.filter((l) => l.id !== id))}
          />
        )}
      </Section>

      {/* Percepciones e Impuestos — only meaningful when the compra
          carries a factura. No factura → no percepciones to declare,
          so we hide the entire section (including any drafts the user
          may have entered before flipping the toggle off). */}
      {header.llevaFactura && (
        <Section
          title="Percepciones e Impuestos"
          onAdd={() => setDialog({ kind: 'add-percepcion' })}
          disabled={isNC && !parentCompra}
        >
          {percepciones.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-5 w-5" aria-hidden />}
              title="Sin percepciones o impuestos"
              hint="Opcional — agregá si corresponden a la compra"
            />
          ) : (
            <PercepcionesTable
              rows={percepciones}
              moneda={moneda}
              onEdit={(p) => setDialog({ kind: 'edit-percepcion', percepcion: p })}
              onRemove={(id) => setPercepciones((prev) => prev.filter((p) => p.id !== id))}
            />
          )}
        </Section>
      )}

      {/* Retenciones section — only visible when llevaFactura, AND only
          for countries whose tax regime uses retenciones (AR). MX uses
          CFDI/SAT and has no retenciones flow. PowerApps gated on
          Tggl_FacturaNegro_CO; we additionally gate on country.features.
          The Agregar button is gated on proveedor + numero
          (Screen_Compras.pa.yaml:1795) because the retencion calc
          aggregates this proveedor's prior-month compras + retenciones
          and anchors each row to the current numero de comprobante. */}
      {showRetenciones && header.llevaFactura && (() => {
        const retencionPrereqsMissing =
          !header.proveedor || !header.numeroComprobante.trim();
        const ncBlocked = isNC && !parentCompra;
        const disabledReason = ncBlocked
          ? 'Seleccioná primero la factura a la que se aplica la NC'
          : retencionPrereqsMissing
            ? 'Completá proveedor y Nº de comprobante antes de agregar retenciones'
            : undefined;
        return (
          <Section
            title="Retenciones"
            onAdd={() => setDialog({ kind: 'add-retencion' })}
            disabled={ncBlocked || retencionPrereqsMissing}
            disabledReason={disabledReason}
          >
            {retenciones.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-5 w-5" aria-hidden />}
                title={
                  retencionPrereqsMissing
                    ? 'Completá proveedor y número de comprobante primero'
                    : 'Sin retenciones cargadas'
                }
                hint={
                  retencionPrereqsMissing
                    ? 'La retención necesita al proveedor y al Nº de comprobante para calcular el importe acumulado del mes.'
                    : COMPROBANTES_EXENTOS_RETENCION.has(header.comprobante)
                      ? 'No requeridas para este tipo de comprobante.'
                      : 'Agregá las retenciones que corresponden a esta factura.'
                }
              />
            ) : (
              <RetencionesTable
                rows={retenciones}
                onEdit={(r) =>
                  setDialog({ kind: 'edit-retencion', retencion: r })
                }
                onRemove={(id) =>
                  setRetenciones((prev) => prev.filter((r) => r.id !== id))
                }
              />
            )}
          </Section>
        );
      })()}

      {/* Footer */}
      <div className="sticky bottom-0 mt-2 flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <TotalPill label="Total Bruto">{formatMoney(totales.totalBruto, moneda)}</TotalPill>
          {/* Total Impuestos = IVA + percepciones, both of which are
              forced to 0 when the compra has no factura — hide the
              pill in that case so the footer doesn't carry a stale
              `$0,00` next to the bruto total. */}
          {header.llevaFactura && (
            <TotalPill label="Total Impuestos">
              {formatMoney(totales.totalImpuestos, moneda)}
            </TotalPill>
          )}
          {/* Retenciones total — always in ARS regardless of compra
              moneda (PowerApps stores `ImporteRetenido_RP` in pesos).
              Surfaces here mainly for edit mode where the admin wants
              to see what's been withheld at a glance, but it's also
              useful while staging retenciones in create mode. */}
          {retenciones.length > 0 && (
            <TotalPill label={`Retenciones${moneda === 'USD' ? ' (ARS)' : ''}`}>
              {formatMoney(
                retenciones.reduce((s, r) => s + (r.importeRetenido || 0), 0),
                'ARS',
              )}
            </TotalPill>
          )}
          <TotalPill label="Total" emphasize>
            {formatMoney(totales.total, moneda)}
          </TotalPill>
        </div>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!canSubmit}
          title={validationError ?? undefined}
          className="gap-2 bg-foreground px-5 text-background hover:bg-foreground/90"
        >
          {isEdit ? 'Guardar cambios' : isNC ? 'Registrar NC' : 'Confirmar compra'}
          <Check className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <ConfirmarCompraDialog
        open={confirmOpen}
        mode={isEdit ? 'edit' : isNC ? 'nc' : 'create'}
        proveedor={header.proveedor?.fields.Nombre_P ?? ''}
        comprobante={header.comprobante}
        numero={header.numeroComprobante}
        moneda={moneda}
        total={totales.total}
        lineCount={lines.length}
        retencionCount={retenciones.length}
        pending={mutation.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          mutation.mutate();
        }}
      />

      {/* Dialogs */}
      {(dialog.kind === 'add-line' || dialog.kind === 'edit-line') && (
        <AgregarProductoDialog
          articulos={articulosQ.data ?? []}
          loading={articulosQ.isPending}
          initial={dialog.kind === 'edit-line' ? dialog.line : null}
          moneda={moneda}
          llevaFactura={header.llevaFactura}
          onCancel={() => setDialog({ kind: 'closed' })}
          onSubmit={(line) => {
            setLines((prev) =>
              line.id && prev.some((l) => l.id === line.id)
                ? prev.map((l) => (l.id === line.id ? line : l))
                : [...prev, line],
            );
            setDialog({ kind: 'closed' });
          }}
        />
      )}
      {(dialog.kind === 'add-percepcion' || dialog.kind === 'edit-percepcion') && (
        <AgregarPercepcionDialog
          catalog={percepcionesCatalogQ.data ?? []}
          loading={percepcionesCatalogQ.isPending}
          initial={dialog.kind === 'edit-percepcion' ? dialog.percepcion : null}
          moneda={moneda}
          onCancel={() => setDialog({ kind: 'closed' })}
          onSubmit={(p) => {
            setPercepciones((prev) =>
              p.id && prev.some((row) => row.id === p.id)
                ? prev.map((row) => (row.id === p.id ? p : row))
                : [...prev, p],
            );
            setDialog({ kind: 'closed' });
          }}
        />
      )}
      {(dialog.kind === 'add-retencion' || dialog.kind === 'edit-retencion') && (
        <AgregarRetencionDialog
          catalog={retencionesCatalogQ.data ?? []}
          loading={retencionesCatalogQ.isPending}
          initial={
            dialog.kind === 'edit-retencion' ? dialog.retencion : null
          }
          defaultNroComprobante={header.numeroComprobante}
          defaultFechaIso={header.fechaComprobante}
          debitoAutomatico={header.debitoAutomatico}
          proveedorNombre={header.proveedor?.fields.Nombre_P?.trim() ?? ''}
          moneda={header.moneda}
          tipoCambio={parseMoney(header.tipoCambio) ?? null}
          totalSinIVA={totales.totalBruto}
          onCancel={() => setDialog({ kind: 'closed' })}
          onSubmit={(r) => {
            setRetenciones((prev) =>
              prev.some((x) => x.id === r.id)
                ? prev.map((x) => (x.id === r.id ? r : x))
                : [...prev, r],
            );
            setDialog({ kind: 'closed' });
          }}
        />
      )}

      <ErrorDialog
        open={pendingFacturaSizeError !== null}
        message={pendingFacturaSizeError ?? ''}
        title="Archivo demasiado grande"
        onClose={() => setPendingFacturaSizeError(null)}
      />

      {pendingFacturaPreviewOpen && pendingFactura && pendingFacturaUrl && (
        <Dialog
          open
          onOpenChange={(o) => !o && setPendingFacturaPreviewOpen(false)}
        >
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="truncate">{pendingFactura.name}</DialogTitle>
              <DialogDescription className="sr-only">
                Vista previa del archivo a adjuntar.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="p-0">
              {pendingFactura.type.startsWith('image/') ? (
                <img
                  src={pendingFacturaUrl}
                  alt={pendingFactura.name}
                  className="max-h-[70vh] w-full object-contain"
                />
              ) : pendingFactura.type === 'application/pdf' ? (
                <iframe
                  src={pendingFacturaUrl}
                  title={pendingFactura.name}
                  className="h-[70vh] w-full border-0"
                />
              ) : (
                <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                  No se puede previsualizar este tipo de archivo.
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(pendingFacturaUrl, '_blank')}
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" aria-hidden />
                Abrir en pestaña nueva
              </Button>
              <Button
                type="button"
                onClick={() => setPendingFacturaPreviewOpen(false)}
              >
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {postCreate && (
        <PostCreateChoiceDialog
          open
          proveedor={postCreate.proveedor}
          numero={postCreate.numero}
          onPagarLuego={() => {
            setPostCreate(null);
            navigate('/compras');
          }}
          onPagarAhora={() => {
            const id = postCreate.compraId;
            setPostCreate(null);
            navigate(`/administracion/pagar/${id}`);
          }}
        />
      )}
      {mutation.isPending && (
        <ProcessingOverlay
          message={isEdit ? 'Guardando cambios…' : isNC ? 'Registrando NC…' : 'Registrando compra…'}
        />
      )}
    </div>
  );
}

/**
 * Pre-submit confirmation. Surfaces proveedor / comprobante / total
 * and item counts so the admin can catch a misclick before the
 * mutation fires. Visually consistent with `ConfirmarPagoDialog` in
 * Pagar Factura — emerald Check badge, footer = Cancelar +
 * primary action — and adapts copy for create / edit / NC.
 */
function ConfirmarCompraDialog({
  open,
  mode,
  proveedor,
  comprobante,
  numero,
  moneda,
  total,
  lineCount,
  retencionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  mode: 'create' | 'edit' | 'nc';
  proveedor: string;
  comprobante: string;
  numero: string;
  /** Display code already mapped from `Pesos Argentinos`/`Dolares`. */
  moneda: 'ARS' | 'MXN' | 'USD';
  total: number;
  lineCount: number;
  retencionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titulo =
    mode === 'edit'
      ? 'Guardar cambios'
      : mode === 'nc'
        ? 'Registrar Nota de Crédito'
        : 'Confirmar compra';
  const accion =
    mode === 'edit'
      ? 'Guardar cambios'
      : mode === 'nc'
        ? 'Registrar NC'
        : 'Confirmar compra';
  const descripcion =
    mode === 'edit'
      ? 'Vas a guardar los cambios sobre esta compra.'
      : mode === 'nc'
        ? 'Vas a registrar una Nota de Crédito.'
        : 'Vas a registrar la siguiente compra:';
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !pending && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Check className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>{titulo}</DialogTitle>
              <DialogDescription>{descripcion}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5">
            {proveedor && (
              <>
                <dt className="text-muted-foreground">Proveedor</dt>
                <dd className="text-right font-medium text-foreground">{proveedor}</dd>
              </>
            )}
            {comprobante && (
              <>
                <dt className="text-muted-foreground">Comprobante</dt>
                <dd className="text-right text-foreground">
                  {comprobante}
                  {numero ? (
                    <>
                      {' '}
                      <span>{numero}</span>
                    </>
                  ) : null}
                </dd>
              </>
            )}
            <dt className="text-muted-foreground">Ítems</dt>
            <dd className="text-right text-foreground">{lineCount}</dd>
            {retencionCount > 0 && (
              <>
                <dt className="text-muted-foreground">Retenciones</dt>
                <dd className="text-right text-foreground">{retencionCount}</dd>
              </>
            )}
            <dt className="text-muted-foreground">Total</dt>
            <dd className="text-right font-semibold text-foreground">
              {formatMoney(total, moneda)}
            </dd>
          </dl>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            {accion}
            <Check className="h-4 w-4" aria-hidden />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Post-creation navigation prompt — surfaces the
 * "Continuar con el Pago" / "Pagar Luego" choice that PowerApps offered
 * inside `PopUp_ConfirmarCompra`. Both buttons leave this page; the
 * dialog isn't dismissible without a choice (closing it via Escape /
 * outside-click defaults to "Pagar Luego" since both paths are valid).
 */
function PostCreateChoiceDialog({
  open,
  proveedor,
  numero,
  onPagarLuego,
  onPagarAhora,
}: {
  open: boolean;
  proveedor: string;
  numero: string;
  onPagarLuego: () => void;
  onPagarAhora: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onPagarLuego()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Check className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Compra registrada</DialogTitle>
              <DialogDescription>
                {proveedor && (
                  <>
                    Se registró la compra de{' '}
                    <span className="font-medium text-foreground">{proveedor}</span>
                  </>
                )}
                {numero && (
                  <>
                    {' '}(comprobante{' '}
                    <span className="text-foreground">{numero}</span>)
                  </>
                )}
                . ¿Querés continuar con el pago ahora?
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onPagarLuego}>
            Pagar Luego
          </Button>
          <Button
            type="button"
            onClick={onPagarAhora}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Continuar con el Pago
            <ReceiptText className="h-4 w-4" aria-hidden />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Line dialog
// ---------------------------------------------------------------------------

/**
 * Matches PowerApps `Screen_ComprobanteCompras` Agregar Servicio/Producto
 * popup: a three-level cascade over `99.ABM_Articulos` where
 * `Comprable_ART='SI'`:
 *
 *   1. Categoria (`Categoria_ART`) — distinct values
 *   2. Cuenta Contable (`CuentaContable_ART`) — distinct values within
 *      the picked Categoria
 *   3. Descripción (the article itself, `Articulo_ART`) — the row within
 *      the picked Categoria + CuentaContable
 *
 * Precio is NOT auto-filled from the article's `ValorUn_ART` — compra
 * prices are negotiated per transaction, not catalog-driven. The IVA
 * rate defaults to the article's `IVA_ART` as a suggestion but stays
 * editable. Observaciones is optional (user relaxed the PowerApps
 * all-fields-required rule).
 */
function AgregarProductoDialog({
  articulos,
  loading,
  initial,
  moneda,
  llevaFactura,
  onCancel,
  onSubmit,
}: {
  articulos: ArticuloItem[];
  loading: boolean;
  /** When present, the dialog opens in edit mode seeded from the row. */
  initial: LineDraft | null;
  /** Compra currency — drives the precio MoneyInput format. */
  moneda: 'ARS' | 'MXN' | 'USD';
  /** When false the line is IVA Exento and the rate is locked to 0 —
   *  no factura means no IVA accrues regardless of the article's
   *  catalog default. Mirrors the header-level IVA guard at QA #17. */
  llevaFactura: boolean;
  onCancel: () => void;
  onSubmit: (line: LineDraft) => void;
}) {
  const isEdit = initial !== null;
  const { config: activeCountry } = useActiveCountry();
  const [categoria, setCategoria] = useState<string>(initial?.productoServicio ?? '');
  const [cuentaContable, setCuentaContable] = useState<string>(initial?.centroCosto ?? '');
  const [articulo, setArticulo] = useState<ArticuloItem | null>(() =>
    initial ? resolveArticulo(articulos, initial) : null,
  );
  const [cantidad, setCantidad] = useState(initial ? formatNumberPlain(initial.cantidad) : '1');
  const [precio, setPrecio] = useState(initial ? formatNumberPlain(initial.precio) : '');
  const [ivaRate, setIvaRate] = useState<number>(
    llevaFactura ? (initial?.ivaRate ?? activeCountry.ivaRateDefault) : 0,
  );
  // Tracks the MX SAT classification alongside the numeric rate.
  // Importacion shares the 16% rate but emits a separate column in the
  // IVA Compras report (and is queued for the CFDI 4.0 + pedimento
  // wiring per the memory note). AR + general 16% lines stay 'general'.
  const [ivaCategoria, setIvaCategoria] = useState<'general' | 'importacion'>(
    initial?.ivaCategoria ?? 'general',
  );

  // Pin to 0 / general whenever Lleva Factura flips off mid-session —
  // the header toggle is outside the dialog, so stale values from a
  // previous open could otherwise persist when the user re-opens.
  useEffect(() => {
    if (!llevaFactura) {
      if (ivaRate !== 0) setIvaRate(0);
      if (ivaCategoria !== 'general') setIvaCategoria('general');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llevaFactura]);
  const [observaciones, setObservaciones] = useState(initial?.observaciones ?? '');
  // Edit mode: the articulos catalog typically finishes loading after the
  // dialog mounts. Re-run the resolver once it's available so the picker
  // pre-fills with the saved article (matched by id when the line carries
  // articuloId, otherwise by name against `Descripcion_PSC`).
  useEffect(() => {
    if (!initial || articulo) return;
    const match = resolveArticulo(articulos, initial);
    if (match) setArticulo(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articulos, initial?.articuloId, initial?.descripcion]);

  // Step 1 options: distinct Categoria_ART across all comprable articles.
  const categorias = useMemo(() => {
    const set = new Set<string>();
    for (const a of articulos) {
      const c = (a.fields.Categoria_ART ?? '').trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [articulos]);

  // Step 2 options: distinct CuentaContable_ART within the selected Categoria.
  const cuentasContables = useMemo(() => {
    if (!categoria) return [];
    const set = new Set<string>();
    for (const a of articulos) {
      if ((a.fields.Categoria_ART ?? '').trim() !== categoria) continue;
      const cc = (a.fields.CuentaContable_ART ?? '').trim();
      if (cc) set.add(cc);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [articulos, categoria]);

  // Step 3 options: the actual articles within Categoria + CuentaContable.
  const articulosFiltrados = useMemo(() => {
    if (!categoria || !cuentaContable) return [];
    return articulos.filter(
      (a) =>
        (a.fields.Categoria_ART ?? '').trim() === categoria &&
        (a.fields.CuentaContable_ART ?? '').trim() === cuentaContable,
    );
  }, [articulos, categoria, cuentaContable]);

  // Cascade reset is handled inline in the Select's onValueChange below —
  // a useEffect on [categoria] would also fire on the initial seed when
  // editing a row, wiping the Cuenta Contable + Articulo we just seeded.

  // When an article is picked, suggest its IVA rate. Precio stays manual.
  //
  // **Edit-mode guard**: this effect ALSO fires when the catalog finishes
  // loading and the prior effect resolves `articulo` from the line's saved
  // descripcion/articuloId. Without the guard, the article's catalog-default
  // IVA would clobber the line's saved `ivaRate` every time the dialog
  // opened in edit mode. Skip the suggestion when the resolved article
  // matches the line's saved article (compare via the same resolver the
  // seeding uses, since legacy lines lack an explicit articuloId).
  useEffect(() => {
    if (!articulo) return;
    // Without factura the rate stays 0 — never adopt the article's
    // catalog IVA suggestion.
    if (!llevaFactura) return;
    if (initial) {
      const seeded = resolveArticulo(articulos, initial);
      if (seeded && seeded.id === articulo.id) return;
    }
    // Adopt the article's catalog IVA rate as a suggestion. Parse
    // tolerantly so AR rows ("21" / "10,5"), MX rows ("16" / "8") and
    // legacy stored shapes ("21%", "0", "Exento") all map cleanly. The
    // `activeCountry.ivaRateDefault` fallback only applies if the
    // article has no parsable rate stored.
    const articleRate = parseIvaRate(
      articulo.fields.IVA_ART,
      activeCountry.ivaRateDefault,
    );
    setIvaRate(articleRate);
    // Keep `ivaCategoria` consistent with the new rate. 'importacion'
    // only makes sense at the 16% bracket — if the new article snaps
    // the rate away from 16, drop the import flag so the form never
    // sits in an invalid `(rate=8, categoria='importacion')` state.
    // The article catalog has no notion of categoria, so we don't
    // PROPOSE importacion from here — only sanitize away an obsolete
    // one. Hitting the same 16% rate leaves the user's manual choice
    // untouched.
    if (articleRate !== 16) setIvaCategoria('general');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articulo]);

  const cantidadN = parseMoney(cantidad) ?? 0;
  const precioN = parseMoney(precio) ?? 0;
  const subtotal = cantidadN * precioN;
  const iva = (subtotal * ivaRate) / 100;
  const total = subtotal + iva;
  const valid =
    Boolean(categoria) &&
    Boolean(cuentaContable) &&
    articulo !== null &&
    cantidadN > 0 &&
    precioN >= 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Servicio / Producto' : 'Agregar Servicio / Producto'}
          </DialogTitle>
          <DialogDescription>
            Seleccioná Categoría y Cuenta Contable para acotar el artículo.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {/*
           * Reserve a 2-line slot for the label in every column so the
           * Select/Combobox beneath stays on the same Y baseline even
           * when the middle label ("Subcategoría / Cuenta Contable")
           * wraps. `leading-tight` keeps the two lines snug;
           * `items-start` anchors single-line labels at the top of the
           * reserved slot.
           */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label className="flex min-h-10 items-start leading-tight">Categoría</Label>
              <Select
                value={categoria}
                onValueChange={(v) => {
                  setCategoria(v);
                  // Clear downstream on user-driven change. Edit-mode seeds
                  // stay intact because this handler only fires on input,
                  // not on the initial useState call.
                  setCuentaContable('');
                  setArticulo(null);
                }}
                disabled={loading || categorias.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      loading
                        ? 'Cargando…'
                        : categorias.length === 0
                          ? 'Sin categorías disponibles'
                          : 'Seleccionar…'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="flex min-h-10 items-start leading-tight">
                Subcategoría / Cuenta Contable
              </Label>
              <Select
                value={cuentaContable}
                onValueChange={(v) => {
                  setCuentaContable(v);
                  setArticulo(null);
                }}
                disabled={!categoria || cuentasContables.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={!categoria ? 'Elegí categoría' : 'Seleccionar…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {cuentasContables.map((cc) => (
                    <SelectItem key={cc} value={cc}>
                      {cc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="flex min-h-10 items-start leading-tight">Descripción</Label>
              <Combobox
                value={articulo}
                onChange={setArticulo}
                items={articulosFiltrados}
                getKey={(a) => a.id}
                getLabel={(a) => a.fields.Articulo_ART?.trim() || '—'}
                getSublabel={(a) => a.fields.Codigo_ART}
                matcher={(a, q) =>
                  (a.fields.Articulo_ART ?? '').toLowerCase().includes(q) ||
                  (a.fields.Codigo_ART ?? '').toLowerCase().includes(q)
                }
                disabled={!cuentaContable}
                placeholder={
                  !cuentaContable ? 'Elegí cuenta contable' : 'Ingresar Desc.'
                }
                className="h-12"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="line-cantidad">Cantidad</Label>
              <Input
                id="line-cantidad"
                variant="filled"
                type="text"
                inputMode="decimal"
                value={cantidad}
                onChange={(e) => setCantidad(sanitizeDecimal(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="line-precio">Precio Unitario</Label>
              <MoneyInput
                id="line-precio"
                variant="filled"
                placeholder="Ingresar Precio Un."
                value={precio}
                moneda={moneda}
                onChange={setPrecio}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Monto Total</Label>
              <Input
                variant="filled"
                readOnly
                value={formatNumber(subtotal)}
                className="cursor-not-allowed text-muted-foreground"
              />
            </div>
            <div className="space-y-1.5">
              {/* Keep the Label as the outer element — same structure as
                  Cantidad / Precio / Monto Total — so the field's
                  vertical rhythm matches. The Info icon lives inside
                  via an inline-flex wrapper, which doesn't add a row. */}
              <Label className="inline-flex items-center gap-1.5">
                %IVA
                {!llevaFactura && (
                  <span
                    className="inline-flex text-muted-foreground"
                    title="Sin factura — IVA Exento aplica a todas las líneas."
                    aria-label="Sin factura — IVA Exento aplica a todas las líneas."
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden />
                  </span>
                )}
              </Label>
              <Select
                value={
                  ivaRate === 0
                    ? '0'
                    : ivaOptionValueFor(ivaRate, ivaCategoria)
                }
                onValueChange={(v) => {
                  // The composite key carries both the numeric rate
                  // and the SAT categoria. Decompose so the line
                  // stores them separately.
                  if (v === '0') {
                    setIvaRate(0);
                    setIvaCategoria('general');
                    return;
                  }
                  const opt = getCompraIvaOptions(activeCountry).find(
                    (o) => o.value === v,
                  );
                  if (!opt) return;
                  setIvaRate(opt.rate);
                  setIvaCategoria(opt.categoria);
                }}
                // Locked to "IVA Exento" when the compra doesn't carry
                // factura — there's no IVA to accrue, so the rate
                // shouldn't be editable at the line level either.
                disabled={!llevaFactura}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getCompraIvaOptions(activeCountry).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="0">IVA Exento</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="line-obs">Observaciones</Label>
            <Textarea
              id="line-obs"
              variant="filled"
              rows={2}
              placeholder="Ingresar Observación (opcional)"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3 text-sm">
            <PreviewPill label="Subtotal">{formatNumber(subtotal)}</PreviewPill>
            {llevaFactura && (
              <PreviewPill label="IVA">{formatNumber(iva)}</PreviewPill>
            )}
            <PreviewPill label="Total" emphasize>
              {formatNumber(total)}
            </PreviewPill>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!valid}
            onClick={() => {
              if (!articulo) return;
              onSubmit({
                id: initial?.id ?? crypto.randomUUID(),
                articuloId: articulo.id,
                productoServicio: categoria,
                centroCosto: cuentaContable,
                descripcion: articulo.fields.Articulo_ART?.trim() ?? '—',
                observaciones: observaciones.trim(),
                cantidad: cantidadN,
                precio: precioN,
                ivaRate,
                ivaCategoria,
                subtotal: round2(subtotal),
                iva: round2(iva),
                total: round2(total),
              });
            }}
          >
            {isEdit ? 'Guardar' : 'Agregar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Percepción dialog
// ---------------------------------------------------------------------------

function AgregarPercepcionDialog({
  catalog,
  loading,
  initial,
  moneda,
  onCancel,
  onSubmit,
}: {
  catalog: PercepcionImpuestoItem[];
  loading: boolean;
  initial: PercepcionDraft | null;
  /** Compra currency — drives the importe MoneyInput format. */
  moneda: 'ARS' | 'MXN' | 'USD';
  onCancel: () => void;
  onSubmit: (p: PercepcionDraft) => void;
}) {
  const isEdit = initial !== null;
  const [selected, setSelected] = useState<PercepcionImpuestoItem | null>(() =>
    initial
      ? (catalog.find(
          (c) => (c.fields.PercepcionImpuesto_PI ?? '').trim() === initial.percepcionImpuesto,
        ) ?? null)
      : null,
  );
  const [importe, setImporte] = useState(initial ? formatNumberPlain(initial.importe) : '');
  const [observaciones, setObservaciones] = useState(initial?.observaciones ?? '');
  // If the catalog finishes loading after the dialog mounts (edit mode),
  // resolve the Combobox selection once the matching entry is available.
  useEffect(() => {
    if (!initial || selected) return;
    const match = catalog.find(
      (c) => (c.fields.PercepcionImpuesto_PI ?? '').trim() === initial.percepcionImpuesto,
    );
    if (match) setSelected(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, initial?.percepcionImpuesto]);

  const importeN = parseMoney(importe) ?? 0;
  const valid = selected !== null && importeN > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar percepción o impuesto' : 'Agregar percepción o impuesto'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label>Percepción / Impuesto</Label>
            <Combobox
              value={selected}
              onChange={setSelected}
              items={catalog}
              getKey={(p) => p.id}
              getLabel={(p) => p.fields.PercepcionImpuesto_PI?.trim() || '—'}
              matcher={(p, q) =>
                (p.fields.PercepcionImpuesto_PI ?? '').toLowerCase().includes(q)
              }
              placeholder={loading ? 'Cargando…' : 'Seleccionar…'}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="perc-importe">Importe</Label>
            <MoneyInput
              id="perc-importe"
              variant="filled"
              placeholder="0,00"
              value={importe}
              moneda={moneda}
              onChange={setImporte}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="perc-obs">Observaciones</Label>
            <Input
              id="perc-obs"
              variant="filled"
              placeholder="Opcional"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!valid}
            onClick={() => {
              if (!selected) return;
              onSubmit({
                id: initial?.id ?? crypto.randomUUID(),
                percepcionImpuesto: selected.fields.PercepcionImpuesto_PI?.trim() ?? '',
                importe: round2(importeN),
                observaciones,
              });
            }}
          >
            {isEdit ? 'Guardar' : 'Agregar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Line / percepción tables
// ---------------------------------------------------------------------------

function ProductoTable({
  lines,
  moneda,
  llevaFactura,
  onEdit,
  onRemove,
}: {
  lines: LineDraft[];
  moneda: 'ARS' | 'MXN' | 'USD';
  /** When false, IVA isn't applied — column shows "—" and Total = Subtotal. QA #17. */
  llevaFactura: boolean;
  onEdit: (line: LineDraft) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <Th>Artículo</Th>
            <Th className="text-right">Cantidad</Th>
            <Th className="text-right">Precio</Th>
            <Th className="text-right">IVA</Th>
            <Th className="text-right">Subtotal</Th>
            <Th className="text-right">Total</Th>
            <Th className="w-20" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {lines.map((l) => (
            <tr key={l.id}>
              <Td>
                <div className="font-medium text-foreground">{l.descripcion}</div>
                <div className="text-xs text-muted-foreground">
                  {l.productoServicio} <span className="mx-1">›</span> {l.centroCosto}
                  {l.observaciones ? ` · ${l.observaciones}` : ''}
                </div>
              </Td>
              <Td className="text-right">{formatNumber(l.cantidad)}</Td>
              <Td className="text-right">{formatMoney(l.precio, moneda)}</Td>
              <Td className="text-right">
                {llevaFactura
                  ? `${formatIvaRate(l.ivaRate)}%${l.ivaCategoria === 'importacion' ? ' Imp.' : ''}`
                  : '—'}
              </Td>
              <Td className="text-right">{formatMoney(l.subtotal, moneda)}</Td>
              <Td className="text-right font-semibold">
                {formatMoney(llevaFactura ? l.total : l.subtotal, moneda)}
              </Td>
              <Td>
                <div className="inline-flex items-center gap-1">
                  <RowEditButton onClick={() => onEdit(l)} />
                  <RowRemoveButton onClick={() => onRemove(l.id)} label="Quitar línea" />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PercepcionesTable({
  rows,
  moneda,
  onEdit,
  onRemove,
}: {
  rows: PercepcionDraft[];
  moneda: 'ARS' | 'MXN' | 'USD';
  onEdit: (p: PercepcionDraft) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <Th>Percepción / Impuesto</Th>
            <Th>Observaciones</Th>
            <Th className="text-right">Importe</Th>
            <Th className="w-20" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((p) => (
            <tr key={p.id}>
              <Td className="font-medium text-foreground">{p.percepcionImpuesto}</Td>
              <Td className="text-muted-foreground">{p.observaciones || '—'}</Td>
              <Td className="text-right font-semibold">{formatMoney(p.importe, moneda)}</Td>
              <Td>
                <div className="inline-flex items-center gap-1">
                  <RowEditButton onClick={() => onEdit(p)} />
                  <RowRemoveButton onClick={() => onRemove(p.id)} label="Quitar percepción" />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetencionesTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: RetencionDraft[];
  onEdit: (r: RetencionDraft) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <Th>Tipo</Th>
            <Th>Concepto</Th>
            <Th>Nº Comprobante</Th>
            <Th>Estado</Th>
            <Th className="text-right">Importe Base</Th>
            <Th className="text-right">Importe Retenido</Th>
            <Th className="w-20" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => {
            // Hydrated-from-SP rows are read-only here. The edit
            // endpoint doesn't accept retenciones; allowing changes
            // would silently lose them on save. Pendiente rows can
            // still be reworked from the Pagar Factura screen.
            const seeded = !!r.existingId;
            const isPaid = (r.status ?? '') === 'Activo';
            return (
              <tr key={r.id}>
                <Td className="font-medium text-foreground">{r.tipo}</Td>
                <Td className="max-w-[320px] text-muted-foreground">
                  <span
                    className="block truncate"
                    title={r.concepto?.fields.ConcatCodigoRegimenConcepto_R ?? ''}
                  >
                    {r.concepto?.fields.ConcatCodigoRegimenConcepto_R ?? '—'}
                  </span>
                </Td>
                <Td className="text-muted-foreground">{r.nroComprobante || '—'}</Td>
                <Td>
                  {seeded ? (
                    <span
                      className={cn(
                        'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium',
                        isPaid
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-900',
                      )}
                      title={
                        isPaid
                          ? 'Retención confirmada en un pago — no editable desde acá.'
                          : 'Retención cargada al crear la compra — se confirma al pagar.'
                      }
                    >
                      {isPaid ? 'Pagada' : 'Pendiente'}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Nueva</span>
                  )}
                </Td>
                <Td className="text-right">{formatMoney(r.importeISAR, 'ARS')}</Td>
                <Td className="text-right font-semibold">
                  {formatMoney(r.importeRetenido, 'ARS')}
                </Td>
                <Td>
                  {seeded ? (
                    // Read-only hint — keep the column the same width
                    // as a normal row's edit/remove buttons so the
                    // table doesn't reflow.
                    <span className="text-[11px] text-muted-foreground">—</span>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <RowEditButton onClick={() => onEdit(r)} />
                      <RowRemoveButton
                        onClick={() => onRemove(r.id)}
                        label="Quitar retención"
                      />
                    </div>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Agregar Retención dialog. Mirrors the PowerApps flow:
 *   - Tipo (Ganancias / Ingresos Brutos / IVA / Seguridad Social / Otros)
 *   - Concepto (searchable combobox over `ABM_Retenciones`)
 *   - Descripción (auto-filled "Sin Retención" when Débito Automático is ON)
 *   - Nº Comprobante (defaults to the compra's number)
 *   - Fecha Comprobante (defaults to the compra's fecha)
 *   - Importe ISAR + Importe Retenido — auto-computed via the preview
 *     endpoint when a concepto is picked; with DA both default to 0.
 *
 * The admin can manually override any calculated amount — the preview
 * just seeds the fields, it doesn't lock them.
 */
function AgregarRetencionDialog({
  catalog,
  loading,
  initial,
  defaultNroComprobante,
  defaultFechaIso,
  debitoAutomatico,
  proveedorNombre,
  moneda,
  tipoCambio,
  totalSinIVA,
  onCancel,
  onSubmit,
}: {
  catalog: RetencionItem[];
  loading: boolean;
  initial: RetencionDraft | null;
  defaultNroComprobante: string;
  defaultFechaIso: string;
  debitoAutomatico: boolean;
  proveedorNombre: string;
  moneda: 'Pesos Argentinos' | 'Pesos Mexicanos' | 'Dolares' | '';
  tipoCambio: number | null;
  totalSinIVA: number;
  onCancel: () => void;
  onSubmit: (r: RetencionDraft) => void;
}) {
  const isEdit = initial !== null;
  const [tipo, setTipo] = useState<TipoRetencion>(
    (initial?.tipo as TipoRetencion) ?? 'Ganancias',
  );
  const [concepto, setConcepto] = useState<RetencionItem | null>(
    initial?.concepto ?? null,
  );
  const [descripcion, setDescripcion] = useState(
    initial?.descripcion ?? (debitoAutomatico ? 'Sin Retención' : ''),
  );
  const [nroComprobante, setNroComprobante] = useState(
    initial?.nroComprobante ?? defaultNroComprobante,
  );
  const [fechaIso, setFechaIso] = useState(
    initial?.fechaComprobanteIso || defaultFechaIso,
  );
  // Importe ISAR is derived — it's this compra's pre-IVA total in pesos
  // (PowerApps `Sum(Collect_ServiciosProductosNuevaCompra, Precio × Cantidad)`
  // at Screen_Compras.pa.yaml:4657, or times TipoCambio for USD). It's
  // NOT something the admin enters; it's what the engine's retention
  // formula uses as the per-compra base.
  const importeISAR_pesos =
    moneda === 'Dolares' ? totalSinIVA * (tipoCambio ?? 0) : totalSinIVA;
  const [importeRetenido, setImporteRetenido] = useState(
    initial
      ? formatNumberPlain(initial.importeRetenido)
      : debitoAutomatico
        ? '0'
        : '',
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Tracked alongside the value so the UI can render a "No corresponde
  // retención" banner when the engine zeroes out the calc, mirroring
  // PowerApps's `PopUp_NoRetencion`. `previewPath` differentiates the
  // two below-threshold cases (monto no sujeto vs. límite mínimo).
  const [previewPath, setPreviewPath] = useState<RetencionPreviewPath | null>(null);
  const [previewReason, setPreviewReason] = useState<string | null>(null);

  const previewMutation = useMutation({ mutationFn: previewRetencion });

  async function runPreview(picked: RetencionItem | null) {
    if (!picked) {
      setPreviewPath(null);
      setPreviewReason(null);
      return;
    }
    // With Debito Automatico the admin has explicitly told us the bank
    // is settling the retention — prefill 0 and skip the preview call.
    if (debitoAutomatico) {
      setImporteRetenido('0');
      setPreviewPath(null);
      setPreviewReason(null);
      return;
    }
    if (!proveedorNombre || !moneda) {
      setPreviewError(
        'Seleccioná proveedor y moneda antes de elegir un concepto para calcular la retención.',
      );
      return;
    }
    // Retenciones régimen is AR-only — the dialog itself only opens when
    // `activeCountry.features.has('retenciones')` is true, so in
    // practice `moneda` can only be 'Pesos Argentinos' | 'Dolares' here.
    // The runtime guard exists to narrow the wider HeaderState union for
    // TypeScript: an MX retención request would 400 server-side anyway,
    // so we bail with a clear message instead of round-tripping.
    if (moneda === 'Pesos Mexicanos') {
      setPreviewError('Las retenciones no aplican a operaciones en MXN.');
      return;
    }
    setPreviewError(null);
    try {
      const res = await previewMutation.mutateAsync({
        draft: {
          proveedorNombre,
          totalSinIVA,
          moneda,
        },
        conceptoConcat: picked.fields.ConcatCodigoRegimenConcepto_R ?? '',
        tipoCambio: tipoCambio ?? undefined,
      });
      if (res.outcome === 'computed') {
        // Only take retenido from the engine — `res.base` is the
        // cumulative monthly base used inside the formula; what we
        // store per-row is this compra's base in pesos (importeISAR_pesos).
        setImporteRetenido(formatNumberPlain(res.retenido));
        setPreviewPath(res.path);
        setPreviewReason(res.reason ?? null);
      } else {
        setPreviewError(res.message);
        setPreviewPath(null);
        setPreviewReason(null);
      }
    } catch (err) {
      setPreviewError(
        err instanceof Error ? err.message : 'No se pudo calcular la retención.',
      );
      setPreviewPath(null);
      setPreviewReason(null);
    }
  }

  const noCorresponde =
    !!previewPath && RETENCION_NO_CORRESPONDE_PATHS.has(previewPath);
  const retenidoN = parseMoney(importeRetenido) ?? 0;
  // Three legitimate ways to land on retenido = 0:
  //   - Débito Automático on (the bank settles the retention)
  //   - Engine flagged "no corresponde" (PowerApps `btn_CloseNoRetencion_CO`
  //     still patches the row with `Descripcion: "Sin retención"`,
  //     `ImporteRetenido: "0"`, so we mirror that — the user can save it)
  //   - Otherwise: require > 0 so we don't accept a blank row by mistake.
  const amountsOk =
    debitoAutomatico || noCorresponde ? retenidoN >= 0 : retenidoN > 0;
  const valid =
    concepto !== null &&
    nroComprobante.trim() !== '' &&
    fechaIso !== '' &&
    amountsOk;

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Retención' : 'Agregar Retención'}
          </DialogTitle>
          <DialogDescription>
            Se guarda como Pendiente y aparece en el primer Pagar Factura de esta compra.
            El importe es una estimación: se recalcula con la fecha de pago.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select
                value={tipo}
                onValueChange={(v) => setTipo(v as TipoRetencion)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_RETENCION.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Concepto (Régimen)</Label>
              <Combobox
                value={concepto}
                onChange={(c) => {
                  setConcepto(c);
                  void runPreview(c);
                }}
                items={catalog}
                getKey={(c) => c.id}
                getLabel={(c) => c.fields.ConcatCodigoRegimenConcepto_R?.trim() || '—'}
                getSublabel={(c) => c.fields.Concepto_R}
                matcher={(c, q) =>
                  (c.fields.ConcatCodigoRegimenConcepto_R ?? '')
                    .toLowerCase()
                    .includes(q) ||
                  (c.fields.Concepto_R ?? '').toLowerCase().includes(q) ||
                  (c.fields.CodigoRegimen_R ?? '').toLowerCase().includes(q)
                }
                placeholder={loading ? 'Cargando…' : 'Seleccionar concepto…'}
              />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ret-nro">Nº Comprobante</Label>
              <Input
                id="ret-nro"
                variant="filled"
                value={nroComprobante}
                onChange={(e) => setNroComprobante(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Fecha Comprobante</Label>
              <DatePicker
                value={fechaIso}
                onChange={setFechaIso}
                placeholder="Seleccionar…"
                className="h-12"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ret-desc">Descripción</Label>
            <Input
              id="ret-desc"
              variant="filled"
              placeholder={
                debitoAutomatico
                  ? 'Sin Retención (débito automático)'
                  : 'Opcional'
              }
              // PowerApps `btn_CloseNoRetencion_CO` overwrites whatever
              // the user typed with "Sin retención" — show that value
              // and lock the input so the override isn't a surprise on
              // save.
              value={noCorresponde ? 'Sin retención' : descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              readOnly={noCorresponde}
              disabled={noCorresponde}
            />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ret-isar">Importe ISAR</Label>
              <Input
                id="ret-isar"
                variant="filled"
                type="text"
                value={formatMoney(importeISAR_pesos, 'ARS')}
                readOnly
                disabled
              />
              <p className="text-[11px] text-muted-foreground">
                Total de la compra sin IVA (en pesos). Se calcula automáticamente.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ret-retenido">Importe Retenido</Label>
              <MoneyInput
                id="ret-retenido"
                variant="filled"
                placeholder="0,00"
                // Locked at 0 when no-corresponde — admin must pick a
                // different concepto. Mirrors PowerApps's PopUp_NoRetencion.
                value={noCorresponde ? '0' : importeRetenido}
                moneda="ARS"
                onChange={setImporteRetenido}
                readOnly={noCorresponde}
                disabled={noCorresponde}
              />
            </div>
          </div>
          {previewMutation.isPending && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Calculando retención…
            </p>
          )}
          {previewError && (
            <p className="text-xs text-amber-700">{previewError}</p>
          )}
          {noCorresponde && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">No corresponde retención.</p>
              <p className="mt-0.5">
                {previewReason ??
                  'El cálculo cae por debajo del mínimo configurado.'}
              </p>
              <p className="mt-1.5 text-amber-800">
                Se guardará la retención con descripción{' '}
                <span className="font-medium">“Sin retención”</span> e importe{' '}
                <span className="font-medium">$0</span>.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!valid}
            onClick={() => {
              if (!concepto) return;
              // PowerApps `btn_CloseNoRetencion_CO` stamps these two
              // fields with hardcoded values when the engine flagged
              // no-corresponde — overriding whatever the user typed in
              // descripcion. We replicate that exact patch so the
              // SharePoint row matches what PowerApps would have written.
              onSubmit({
                id: initial?.id ?? crypto.randomUUID(),
                tipo,
                concepto,
                descripcion: noCorresponde ? 'Sin retención' : descripcion,
                nroComprobante: nroComprobante.trim(),
                fechaComprobanteIso: fechaIso,
                importeISAR: round2(importeISAR_pesos),
                importeRetenido: noCorresponde ? 0 : round2(retenidoN),
              });
            }}
          >
            {isEdit ? 'Guardar' : 'Agregar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowEditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Editar"
      title="Editar"
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      <Pencil className="h-4 w-4" aria-hidden />
    </button>
  );
}

function RowRemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive transition-colors hover:bg-destructive/20"
    >
      <Trash2 className="h-4 w-4" aria-hidden />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * Card wrapper for the header form's three logical groups
 * (Identificación / Fechas y moneda / Observaciones). Subtle bg-card,
 * thin border, small uppercase caption — gives the form a clearer
 * visual hierarchy without adding heavy section headers. `gridLess`
 * skips the inner 12-col grid for sections that only contain a single
 * full-width child (e.g. the observaciones textarea).
 */
function FormCard({
  caption,
  gridLess,
  cols = 12,
  children,
}: {
  caption: string;
  gridLess?: boolean;
  /** Inner grid column count on md+. 12 (default) lets fields use
   *  span-N to compose flexible widths. 2 forces a strict 2-per-row
   *  layout — used when two FormCards sit side-by-side and each card
   *  has half the horizontal space. */
  cols?: 2 | 12;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border bg-card p-4 sm:p-5">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {caption}
      </h2>
      {gridLess ? (
        children
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            cols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-12',
          )}
        >
          {children}
        </div>
      )}
    </section>
  );
}

function Section({
  title,
  onAdd,
  disabled,
  disabledReason,
  children,
}: {
  title: string;
  onAdd: () => void;
  disabled: boolean;
  /** Short explanation rendered next to a disabled Agregar button. Keep
   *  it to a single line — the goal is "the user understands why they
   *  can't click without hunting for empty-state text below." */
  disabledReason?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <Button
          type="button"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
          className="gap-2 bg-foreground text-background hover:bg-foreground/90"
        >
          Agregar
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </Button>
        {disabled && disabledReason && (
          <span className="text-xs font-medium text-destructive">
            {disabledReason}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-card/40 px-4 py-10 text-center">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600">
        {icon}
      </span>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function TotalPill({
  label,
  children,
  emphasize,
}: {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-xs',
        emphasize ? 'border-foreground/60 bg-foreground/5' : 'bg-secondary',
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{children}</span>
    </span>
  );
}

function PreviewPill({
  label,
  children,
  emphasize,
}: {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-8 items-center gap-2 rounded-md border px-2.5 text-xs',
        emphasize ? 'border-foreground/60 bg-foreground/5' : 'bg-secondary',
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{children}</span>
    </span>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-2.5 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2.5', className)}>{children}</td>;
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '');
  const match = cleaned.match(/^\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatNumber(n: number): string {
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Seed numeric inputs when opening a dialog in edit mode. Returns a
 * comma-decimal string WITHOUT thousand separators so the value
 * round-trips cleanly through `sanitizeDecimal` + `parseMoney` on
 * subsequent edits. Example: 1500.5 → "1500,5", 1 → "1".
 */
function formatNumberPlain(n: number): string {
  if (!Number.isFinite(n)) return '';
  return String(n).replace('.', ',');
}

/** `yyyy-mm-dd` → local `Date`. */
function isoToDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function dateToIso(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** `yyyy-mm-dd` → `dd/mm/yyyy` (AR SP format). */
function isoToAr(s: string): string {
  const d = isoToDate(s);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** `dd/mm/yyyy` → `yyyy-mm-dd` (DatePicker's contract). */
function arToIso(s: string | null | undefined): string {
  if (!s) return '';
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/**
 * Staged factura uploader. Used in both create mode (no existing file)
 * and edit mode after the user has staged a delete of the existing file.
 * The actual upload runs in the parent's mutation after the row is
 * created/edited; this component only manages the picker UI.
 *
 * `showRestore` surfaces an extra button that lets the user back out of
 * a staged delete in edit mode without committing.
 */
function PendingFacturaUploader({
  pending,
  inputRef,
  llevaFactura,
  showRestore,
  onRestore,
  onPick,
  onClear,
  onPreview,
  onSizeError,
  extracting = false,
}: {
  pending: File | null;
  inputRef: RefObject<HTMLInputElement>;
  llevaFactura: boolean;
  showRestore: boolean;
  onRestore: () => void;
  onPick: (f: File) => void;
  onClear: () => void;
  onPreview: () => void;
  onSizeError: (message: string) => void;
  /** Shown while Gemini extraction is in flight (compact spinner +
   *  "Analizando…" copy) so the user knows the form is about to
   *  autocomplete. Optional — defaults off for callers that don't
   *  wire the extraction flow. */
  extracting?: boolean;
}) {
  // Compact input-style uploader (h-12 to match the rest of the form).
  // Empty state: a single clickable row showing an upload icon and a
  // hint label. Filled state: filename + icon-only actions (preview /
  // replace / remove) so it still fits in one row with no wrapping.
  // While Gemini extraction is in flight, the row picks up an emerald
  // tint + animated ring + a Sparkles "Analizando con IA…" line so the
  // user sees the AI is actually working instead of a tiny spinner.
  return (
    <div
      className={cn(
        'flex h-12 items-center gap-2 rounded-lg border px-3 transition-colors',
        extracting
          ? 'animate-pulse border-emerald-300 bg-emerald-50 ring-2 ring-emerald-200/60'
          : 'border-transparent bg-secondary',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          if (!f) return;
          // 4 MB ceiling to stay under Vercel's 4.5 MB serverless body
          // limit with multipart-envelope headroom.
          if (f.size > 4 * 1024 * 1024) {
            onSizeError(
              `El archivo pesa ${(f.size / (1024 * 1024)).toFixed(1)} MB ` +
                'y el tope permitido es 4 MB. Comprimí el PDF o la imagen ' +
                'antes de volver a intentarlo.',
            );
            e.target.value = '';
            return;
          }
          onPick(f);
        }}
      />
      {pending ? (
        <>
          {extracting ? (
            <span className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center">
              <Sparkles
                className="h-4 w-4 animate-pulse text-emerald-600"
                aria-hidden
              />
            </span>
          ) : (
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
          {extracting ? (
            <span className="flex flex-1 items-baseline gap-1 text-sm font-medium text-emerald-900">
              <span className="whitespace-nowrap">Analizando factura con IA</span>
              <span className="inline-flex">
                <span className="animate-bounce [animation-delay:-0.3s]">.</span>
                <span className="animate-bounce [animation-delay:-0.15s]">.</span>
                <span className="animate-bounce">.</span>
              </span>
            </span>
          ) : (
            <span className="flex-1 truncate text-sm font-medium text-foreground">
              {pending.name}
            </span>
          )}
          {!extracting && (
            <>
              <UploaderIconButton
                onClick={onPreview}
                title="Vista previa"
                ariaLabel="Vista previa"
              >
                <Eye className="h-4 w-4" aria-hidden />
              </UploaderIconButton>
              <UploaderIconButton
                onClick={() => inputRef.current?.click()}
                title="Cambiar archivo"
                ariaLabel="Cambiar archivo"
              >
                <Upload className="h-4 w-4" aria-hidden />
              </UploaderIconButton>
              <UploaderIconButton
                onClick={onClear}
                title="Quitar archivo"
                ariaLabel="Quitar archivo"
                tone="destructive"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </UploaderIconButton>
            </>
          )}
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-full flex-1 items-center gap-2 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Upload className="h-4 w-4 shrink-0" aria-hidden />
            <span>
              {llevaFactura ? 'Subir factura' : 'Subir comprobante / presupuesto'}
            </span>
          </button>
          {showRestore && (
            <UploaderIconButton
              onClick={onRestore}
              title="Mantener el archivo original"
              ariaLabel="Restaurar"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </UploaderIconButton>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Icon-only action button used inside the compact uploader.
 * Stays out of the field's tab order metaphor (just hover-discovery
 * via title/aria-label) and aligns vertically with the h-12 row.
 */
function UploaderIconButton({
  onClick,
  title,
  ariaLabel,
  children,
  tone = 'neutral',
}: {
  onClick: () => void;
  title: string;
  ariaLabel: string;
  children: React.ReactNode;
  tone?: 'neutral' | 'destructive';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className={
        tone === 'destructive'
          ? 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive'
          : 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground'
      }
    >
      {children}
    </button>
  );
}
